/* Pickld cart drawer C4, "Confirm, save and add". Built 2026-10-11; gift ladder,
   dispatch line and pinned offer row added 2026-10-18; gift tiers given one at a
   time (setting stack_tiers off) the same evening, because Shopify applies only one
   of two automatic Buy X Get Y discounts to a cart; the inline variant chooser in
   the offer rows added the same night. On dev (staging), not yet on main.
   Reference: docs/components/cart-c4.md (every part, the data, the gift rules,
   settings, admin objects, switching back, debugging, what is not proven).

   One render path. Liquid (sections/pickld-cart-drawer-c4.liquid) is the DATA
   layer: it emits a small JSON island, <script type="application/json"
   data-c4-meta>, holding only what the Ajax cart JSON cannot say (bulky and
   supplier lines, pre-order deposit and balance, the threshold, the gift tiers,
   the ranked offers after the live guards, the trust line). This file is the
   ONLY place cart markup is built, from the /cart.js JSON shape plus that
   island, and the ONLY place a gift line is added or removed. Nothing is written
   twice, which is what retired the drift in the old drawer (Liquid list plus a
   JS string template that had already diverged).

   Round trips:
     open            GET /cart.js  +  GET /?sections=<id>      (in parallel; paths
                     from Shopify's routes, so a market subfolder works; the
                     section's wallet buttons are swapped in too, 19 Oct)
     qty, remove     POST /cart/change.js {id: key, quantity, sections}
     code            POST /cart/update.js {discount, sections}
     tier-up swap    POST /cart/update.js {updates: {<from line key>: 0, <to variant>: 1}, sections}
     offer add       POST /cart/add.js {items, sections} then GET /cart.js
     variant chooser GET /products/<handle>.js, once per product per page, only
                     when the shopper opens "Other colours" on an offer row
     gift ladder     after any of the above, at most: POST /cart/update.js
                     {updates: {<gift line key>: 0|qty}} and POST /cart/add.js
                     {items: [{id, quantity, properties: {_pk_gift}}]} then
                     GET /cart.js, then one read-only check that every gift is
                     free at Shopify (it is taken off if not)
   Every mutating call asks for the section in the SAME response (bundled
   Section Rendering), so the offers and deposits are recomputed by Liquid
   against the cart that now exists. A bad `sections` value 400s the whole
   request, so the id comes from the section itself (data-section-id).

   Public API kept compatible with the old drawer, so assets/pickld-buy-row.js,
   the PDP and the paddle finder keep working unchanged:
     window.PickldCart.open() .close() .refresh() .add(FormData)

   Money is formatted with the shop's money_format from the island, never a
   hand-typed "$". Codes do not stack on this store, and cart/update.js with a
   new code REPLACES the old one, so applying a code that turns out not to apply
   puts the previous code back.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-c4]');
  if (!root || root.dataset.c4Bound) return;
  root.dataset.c4Bound = '1';

  var sectionId = root.dataset.sectionId;
  var $ = function (sel) { return root.querySelector(sel); };
  var el = {
    panel: $('.c4__panel'),
    close: $('.c4__x'),
    count: $('[data-c4-count]'),
    ship: $('[data-c4-ship]'),
    body: $('[data-c4-body]'),
    foot: $('[data-c4-foot]'),
    pin: $('[data-c4-pin]'),
    code: $('[data-c4-code]'),
    totals: $('[data-c4-totals]'),
    checkout: $('[data-c4-checkout]'),
    express: $('[data-c4-express]'),
    trust: $('[data-c4-trust]'),
    live: $('[data-c4-live]')
  };

  var state = {
    cart: null,
    meta: readMeta(root) || {},
    busy: false,
    codeOpen: false,
    note: null,          // {tone: 'ok'|'warn'|'err', text, keep?}
    giftNote: null,      // one line under the shipping band when a gift could not be added
    lastFocused: null,
    focusKey: null,      // what to put focus back on after the next render
    inerted: [],
    charges: {},         // line key -> per-unit checkout charge from the last POST response
    chooser: null,       // the one open variant chooser: {tag, pid, status: 'loading'|'ready'|'error'}
    choices: {},         // offer tag -> {pid, vid}: the variant the shopper picked in that row
    limits: {}           // lineId -> the quantity Shopify refused to go above (+ is shut there); reset on open
  };

  // The pinned offer row is a phone-only test variant (setting pinned_offer).
  var phoneQuery = window.matchMedia ? window.matchMedia('(max-width: 480px)') : null;

  // Limited-release wording (docs/components/cart-c4.md, section 2.1). The island says which lines
  // carry the section's limited_tag; nothing here names a product.
  var LIMITED = {
    line: 'Limited release',
    lineOther: 'Discount codes don\u2019t apply',
    refused: 'This code doesn’t cover limited-release or launch items. Your code hasn’t been used, so you can keep it for next time.',
    applied: 'Your code has been applied to the items it covers. Limited-release and launch items are excluded.',
    hint: 'Codes don’t cover limited-release or launch items.'
  };

  /* ---------- helpers ---------- */

  // The island is one JSON document. If anything inside it fails to render
  // (a Liquid error string, a bad value) JSON.parse throws: say so in the
  // console and keep the last good island, never drop to null, which would
  // silently lose offers, deposits, bulky flags, gift tiers and the threshold.
  function readMeta(scope) {
    var s = scope.querySelector('script[data-c4-meta]');
    if (!s) return null;
    try { return JSON.parse(s.textContent); } catch (e) {
      if (window.console && console.error) console.error('[pickld-cart-c4] cart data island did not parse; keeping the last good one.', e);
      return null;
    }
  }

  function warn() {
    if (window.console && console.warn) console.warn.apply(console, ['[pickld-cart-c4]'].concat(Array.prototype.slice.call(arguments)));
  }

  function sectionNode(sections) {
    if (!sections || !sections[sectionId]) return null;
    var tmp = document.createElement('div');
    tmp.innerHTML = sections[sectionId];
    return tmp;
  }

  // Locale and market subfolders: every path comes from Shopify's routes
  // (emitted in the island), with Shopify.routes.root as the fallback.
  function routes() {
    var r = (state.meta && state.meta.routes) || {};
    var base = r.root || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (base.slice(-1) !== '/') base += '/';
    return {
      root: base,
      cart: r.cart || base + 'cart',
      add: r.add || base + 'cart/add',
      change: r.change || base + 'cart/change',
      update: r.update || base + 'cart/update'
    };
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Catalogue titles use a spaced hyphen as a separator ("X40 Outdoor
  // Pickleballs - 3 Pack"); the drawer shows it as a comma.
  function nice(t) { return String(t == null ? '' : t).replace(/\s+[-\u2013\u2014]\s+/g, ', '); }

  function cap(t) { t = String(t || ''); return t.charAt(0).toUpperCase() + t.slice(1); }

  function money(cents) {
    var fmt = (state.meta && state.meta.money_format) || '${{amount}}';
    var neg = cents < 0;
    var c = Math.abs(Math.round(cents));
    function group(n, d, t, s) {
      var parts = (n / 100).toFixed(d).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
      return parts.length > 1 ? parts[0] + s + parts[1] : parts[0];
    }
    var out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, k) {
      if (k === 'amount_no_decimals') return group(c, 0, ',', '.');
      if (k === 'amount_with_comma_separator') return group(c, 2, '.', ',');
      if (k === 'amount_no_decimals_with_comma_separator') return group(c, 0, '.', ',');
      return group(c, 2, ',', '.');
    });
    return (neg ? '−' : '') + out;
  }

  function img(url, w) {
    if (!url) return '';
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function sum(arr, fn) { return arr.reduce(function (t, x) { return t + fn(x); }, 0); }

  function api(path, body) {
    var payload = Object.assign({}, body, { sections: sectionId, sections_url: window.location.pathname });
    return fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(parse).then(function (j) { recordCharges(j); return j; });
  }

  // Shopify's error fields are copied onto the Error, so callers that read
  // err.description (main-pickld-product.liquid shows it under the buy button)
  // keep working exactly as they did with the old drawer.
  function parse(r) {
    return r.json().catch(function () { return null; }).then(function (j) {
      if (!r.ok || (j && j.status && j.status >= 400)) {
        var err = new Error((j && (j.description || j.message)) || 'Something went wrong.');
        err.payload = j;
        err.status = (j && j.status) || r.status;
        err.description = (j && (j.description || j.message)) || err.message;
        throw err;
      }
      return j;
    });
  }

  function getCart() {
    return fetch(routes().cart + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(parse);
  }

  // The section as it renders for the cart now: its island and its wallet buttons.
  function getSection() {
    return fetch(routes().root + '?sections=' + encodeURIComponent(sectionId), { credentials: 'same-origin' })
      .then(parse).then(sectionNode);
  }

  /* ---------- discount shapes ---------- */

  // cart_level_discount_applications has two shapes. POST add, change and
  // update responses nest it: {amount, discount_application: {type, title,
  // total_allocated_amount}} (fixtures 03, 05, 14, 17). GET /cart.js returns
  // it flat: {type, title, total_allocated_amount} (fixtures 04, 09, 15).
  // Everything reads it through this one function.
  function app(a) {
    var da = (a && a.discount_application) || a || {};
    var amt = da.total_allocated_amount != null ? da.total_allocated_amount : (a && a.amount) || 0;
    return { type: da.type, title: da.title, amount: amt };
  }
  function lineAlloc(a) {
    var da = (a && a.discount_application) || a || {};
    return { type: da.type, amount: a && a.amount != null ? a.amount : (da.total_allocated_amount || 0) };
  }

  // POST responses carry selling_plan.checkout_charge {value_type, value};
  // GET /cart.js does not (fixtures 14 and 15). Kept per line key so the
  // Liquid deposit can be cross-checked at runtime.
  function recordCharges(j) {
    var items = (j && j.items) || [];
    items.forEach(function (it) {
      var sp = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
      var cc = sp && sp.checkout_charge;
      if (!cc || !it.key) return;
      if (cc.value_type === 'price') state.charges[it.key] = cc.value;
      else if (cc.value_type === 'percentage') state.charges[it.key] = Math.round(it.price * cc.value / 100);
    });
  }

  /* ---------- the numbers (all derived from the /cart.js shape) ---------- */

  // A line's value after every discount, line level AND the line's share of a
  // cart level code. /cart.js keeps final_line_price BEFORE cart level codes
  // (fixture 14: final_line_price 29900, discounts[0].amount 2990), so the
  // cart level share is the line's discounts minus its line level total.
  function lineAfter(item) {
    var all = sum(item.discounts || [], function (d) { return d.amount; });
    var cartLevel = Math.max(0, all - (item.line_level_total_discount || 0));
    return item.final_line_price - cartLevel;
  }

  function lineMeta(item) {
    return (state.meta && state.meta.lines && state.meta.lines[item.key]) || {};
  }

  // A deposit line: a selling plan leaves a balance to charge later. The island
  // gives the per-unit deposit only when it checked out against the plan price
  // (snippets/pickld-c4-line-json.liquid); otherwise deposit is null and the
  // drawer names the deposit without an amount.
  function isDeposit(item) {
    var lm = lineMeta(item);
    return lm.deposit_plan === true || lm.deposit != null;
  }

  function inIsland(item) {
    return !!(state.meta && state.meta.lines && Object.prototype.hasOwnProperty.call(state.meta.lines, item.key));
  }

  function codeSavings(cart) {
    var c = sum(cart.cart_level_discount_applications || [], function (a) {
      var x = app(a);
      return x.type === 'discount_code' ? x.amount : 0;
    });
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (a) {
        var x = lineAlloc(a);
        if (x.type === 'discount_code') c += x.amount;
      });
    });
    return c;
  }

  function promiseOk(cart) {
    var m = state.meta || {};
    if (!m.threshold_cents) return false;
    if (m.threshold_currency && cart.currency !== m.threshold_currency) return false;
    if (m.au_context === false) return false;
    return true;
  }

  // Free AU shipping is judged on what ships in our parcel, after codes: never a
  // bulky line (freight), a gift card, a line that needs no shipping, or a free
  // gift line (it is $0 when it is free, and it must never lift a cart that is
  // not free yet).
  function shipCounts(it) {
    return !lineMeta(it).bulky && !giftOf(it) && !it.gift_card && it.requires_shipping !== false;
  }

  function figures(cart) {
    var m = state.meta || {};
    var chosen = cart.items.filter(function (it) { return !giftOf(it); });
    var pre = [];
    chosen.forEach(function (it) { if (lineMeta(it).preorder) pre.push(it); });
    var eligible = sum(cart.items, function (it) { return shipCounts(it) ? lineAfter(it) : 0; });
    var eligibleBefore = sum(cart.items, function (it) { return shipCounts(it) ? it.final_line_price : 0; });
    var hasBulky = chosen.some(function (it) { return lineMeta(it).bulky; });

    // Deposit lines. The island's deposit is per unit, checked against the plan
    // price in Liquid (null when it did not check out). The POST response's
    // checkout_charge.value is per unit too (fixture 14); when one disagrees
    // with the island, the split is not shown at all.
    var depLines = pre.filter(isDeposit);
    var depositsKnown = depLines.every(function (it) { return lineMeta(it).deposit != null; });
    var mismatch = depLines.some(function (it) {
      var ch = state.charges[it.key];
      return ch != null && lineMeta(it).deposit != null && ch !== lineMeta(it).deposit;
    });
    var deposits = sum(depLines, function (it) { return lineMeta(it).deposit != null ? lineMeta(it).deposit * it.quantity : 0; });

    // The to-the-cent split ("Due today", "Checkout $X today") is printed only
    // once a real staging checkout has settled how a code meets a deposit
    // (deposit_verified). Until then: the deposit, and a plain Checkout.
    var splitShown = depLines.length > 0 && depositsKnown && m.deposit_verified === true && !mismatch;
    var dueToday = null, later = null;
    if (splitShown) {
      var depAfter = sum(depLines, function (it) {
        var d = lineMeta(it).deposit * it.quantity;
        if (m.deposit_split === 'pro_rata' && it.final_line_price > 0) d = Math.round(d * lineAfter(it) / it.final_line_price);
        return d;
      });
      var rest = sum(cart.items.filter(function (it) { return depLines.indexOf(it) === -1; }), lineAfter);
      dueToday = depAfter + rest;
      later = cart.total_price - dueToday;
    }
    var f = {
      subtotal: cart.items_subtotal_price,
      total: cart.total_price,
      saved: codeSavings(cart),
      eligible: eligible,
      eligibleBefore: eligibleBefore,
      hasBulky: hasBulky,
      onlyBulky: chosen.length > 0 && chosen.every(function (it) { return lineMeta(it).bulky; }),
      hasPre: pre.length > 0,
      hasDeposit: depLines.length > 0,
      depositsKnown: depositsKnown,
      deposits: deposits,
      splitShown: splitShown,
      dueToday: dueToday,
      later: later,
      gap: m.threshold_cents ? m.threshold_cents - eligible : null,
      // a free-shipping promise is only made where it is proven true
      promise: promiseOk(cart) && !(depLines.length > 0 && !splitShown)
    };
    f.gift = giftFigures(cart);
    f.giftLines = cart.items.filter(function (it) { var t = tierFor(giftOf(it)); return t && giftFree(cart, it, t); }).length;
    f.steps = ladder(cart, f);
    return f;
  }

  // The one progress bar shows the NEXT step: free AU shipping at the threshold,
  // then each gift tier in threshold order. Each step is measured on its own
  // number (shipping on the parcel, a gift on everything the shopper chose), and
  // a step is only shown while it can actually be delivered: no gift step for a
  // gift that is out of stock, declined, refused by Shopify on this code, or that
  // would have to travel alone (machine-only and bulky-only carts). With tiers not
  // stacking (stack_tiers off), a higher gift REPLACES the lower one, and is not a
  // step at all while the shopper's own units carry a lower tier (giftTarget).
  function ladder(cart, f) {
    var m = state.meta || {};
    var out = [];
    if (promiseOk(cart)) out.push({ kind: 'ship', at: m.threshold_cents, value: f.eligible, before: f.eligibleBefore });
    var ok = giftContextOk(cart) && f.gift.parcel;
    var memo = memoFor(cart);
    var stack = stacking();
    var have = {};
    cart.items.forEach(function (it) { var h = giftOf(it); if (h) have[h] = true; });
    var list = giftTiers();
    list.forEach(function (t, i) {
      var lowerOwn = !stack && list.slice(0, i).some(function (u) { return ownUnits(cart, u) > 0; });
      if (!have[t.handle] && !(ok && t.available && !tierBlocked(memo, t, cart) && !lowerOwn)) return;
      // A deposit line does not count towards a gift, so progress on a deposit cart
      // would read short of what the shopper sees in the total: no gift step there
      // unless the gift is already in (the in-stock lines alone qualified).
      if (f.hasDeposit && !have[t.handle]) return;
      out.push({ kind: 'gift', at: t.threshold_cents, value: f.gift.amount, before: f.gift.before, tier: t, have: !!have[t.handle] });
    });
    out.forEach(function (s) { s.met = s.have || s.value >= s.at; });
    out.sort(function (a, b) { return a.at - b.at || (a.kind === 'ship' ? -1 : 1); });
    return out;
  }

  function codes(cart) {
    var list = cart.discount_codes || [];
    return {
      applied: list.filter(function (c) { return c.applicable; }),
      refused: list.filter(function (c) { return !c.applicable; })
    };
  }

  function limitedLines(cart) {
    return (cart.items || []).filter(function (it) { return lineMeta(it).limited === true; });
  }

  // Did a code take anything off this line, at line level (a code limited to a collection, the
  // shape BEN10 has live) or as the line's share of a cart level code?
  function codeOnLine(item) {
    var lineLevel = sum(item.line_level_discount_allocations || [], function (a) { return lineAlloc(a).amount; }) - autoOff(item);
    var cartLevel = sum(item.discounts || [], function (d) { return d.amount; }) - (item.line_level_total_discount || 0);
    return lineLevel > 0 || cartLevel > 0;
  }

  // A code is on the cart and a limited-release line got nothing from it. Read from the cart on
  // every render, so a code that arrived from an email link, or a reload, says so too. A code that
  // really did cover the line (one not tied to the eligible collections) stays silent: the
  // sentence would be false.
  function codeSkipsLimited(cart) {
    return codes(cart).applied.length > 0 && limitedLines(cart).some(function (it) { return !codeOnLine(it); });
  }

  /* ---------- gift ladder ----------
     docs/components/cart-c4.md, "Gift ladder". The tiers come from the gift_tier
     metaobject through the island (snippets/pickld-c4-gifts.liquid): handle,
     threshold, the variants that count as the gift, the variant to add (most
     available stock), quantity, label, available. Automatic Buy X Get Y discounts
     in Shopify make the lines free; the theme only adds and removes the lines,
     and never trusts that a line is free until the cart says so. */

  function giftOf(item) {
    var v = item && item.properties && item.properties._pk_gift;
    return v ? String(v) : null;
  }

  function giftTiers() {
    var g = (state.meta && state.meta.gifts) || [];
    return g.slice().sort(function (a, b) { return a.threshold_cents - b.threshold_cents; });
  }

  function tierFor(handle) {
    if (!handle) return null;
    return giftTiers().filter(function (t) { return t.handle === handle; })[0] || null;
  }

  function tierVariants(t) {
    var ids = (t && t.variant_ids && t.variant_ids.length) ? t.variant_ids : [t && t.variant_id];
    return ids.map(String);
  }

  // What counts towards a gift tier: every line the shopper chose, after codes.
  // Never the gift lines themselves (Shopify's discount counts them, the theme
  // does not, so a gift can never qualify itself), never a gift card, and never a
  // deposit pre-order line (how Shopify counts a deposit towards an automatic
  // discount minimum is unproven; a selling-plan line missing from the island is
  // treated the same way). Bulky items and ball machines DO count: the qualifying
  // collection gift-ladder-qualifying holds every product with a price.
  function giftCounts(it) {
    if (giftOf(it) || it.gift_card) return false;
    if (isDeposit(it)) return false;
    if (it.selling_plan_allocation && !inIsland(it)) return false;
    return true;
  }

  // A line that leaves in our own parcel (General delivery profile, from our
  // shelf). A gift is only ever added to a cart that already has one, so it never
  // starts a parcel of its own: a machine ships from the supplier and bulky
  // freight ships separately, and a $0 parcel of gifts would be charged the
  // standard rate. A line missing from the island does not count (fail closed).
  function parcelLine(it) {
    if (giftOf(it) || it.gift_card || it.requires_shipping === false || !inIsland(it)) return false;
    var lm = lineMeta(it);
    return !lm.bulky && !lm.supplier;
  }

  // The gift discounts pay out on ANY unit of the listed variants, so once the
  // cart qualifies Shopify may already be making the shopper's OWN overgrips (or
  // Flick Stick) free. That free value is still part of what the shopper chose to
  // buy, so it is added back to the amount a tier is judged on: otherwise a cart
  // that has just qualified would read as under the line again (a $312 paddle
  // plus five paid overgrips is $330, and $312 once three of them go free).
  function giftVariantSet() {
    var s = {};
    giftTiers().forEach(function (t) { tierVariants(t).forEach(function (v) { s[v] = true; }); });
    return s;
  }

  function giftFigures(cart) {
    var gv = giftVariantSet();
    var counted = cart.items.filter(giftCounts);
    var back = function (it) { return gv[String(it.variant_id)] ? autoOff(it) : 0; };
    return {
      amount: sum(counted, function (it) { return lineAfter(it) + back(it); }),
      before: sum(counted, function (it) { return it.final_line_price + back(it); }),
      parcel: cart.items.some(parcelLine)
    };
  }

  // Units of the tier's variants the shopper put in the cart themselves.
  function ownUnits(cart, t) {
    var vs = tierVariants(t);
    return sum(cart.items.filter(function (i) { return !giftOf(i) && vs.indexOf(String(i.variant_id)) > -1; }), function (i) { return i.quantity; });
  }

  // How many units a NEW gift line carries: the tier's quantity less the
  // shopper's own units of those variants. Shopify gives at most the tier's
  // quantity free across all of them, so a full gift line beside the shopper's
  // own overgrips would only move the discount and raise the total. A gift line
  // already in the cart is never shrunk for units the shopper adds later: those
  // they mean to buy.
  function giftNeed(cart, t) { return Math.max(0, t.quantity - ownUnits(cart, t)); }

  // Gift thresholds are AUD and the gift ships in an Australian parcel, so the
  // ladder runs only on an AUD cart in an Australian storefront context, the same
  // conditions as the free-shipping promise.
  function giftContextOk(cart) {
    var m = state.meta || {};
    if (!Array.isArray(m.gifts) || !m.gifts.length) return false;
    if (m.same_currency === false || m.au_context === false) return false;
    if (m.threshold_currency && cart.currency !== m.threshold_currency) return false;
    return true;
  }

  // Every line allocation that is not a discount code. Measured on staging on
  // 18 Oct 2026: /cart.js gives the automatic Buy X Get Y gift allocation
  // discount_application.type "" (empty), not "automatic", so a test for
  // "automatic" read every free gift as charged and the backstop took it off.
  // A code is recognised by its type or by its title matching a cart code.
  function autoOff(it) {
    var codes = ((state.cart && state.cart.discount_codes) || []).map(function (d) { return String(d.code || '').toLowerCase(); });
    return sum(it.line_level_discount_allocations || [], function (a) {
      var x = lineAlloc(a);
      if (x.type === 'discount_code') return 0;
      var title = String((a && a.discount_application && a.discount_application.title) || '').toLowerCase();
      return codes.indexOf(title) > -1 ? 0 : x.amount;
    });
  }

  // Free means: the automatic discounts across every line of the tier's
  // variants cover as many units as Shopify should give away here, which is the
  // tier's quantity or, when fewer are in the cart, all of them. Pooled because
  // Shopify may put the free units on the shopper's own line instead of the gift
  // line; the total charged is the same. Assumes the variants listed in one tier
  // share a price (all four FlexTac colours are $6.00).
  function giftFree(cart, line, t) {
    var vs = tierVariants(t);
    var pool = sum(cart.items.filter(function (i) { return vs.indexOf(String(i.variant_id)) > -1; }), autoOff);
    var unit = line.quantity > 0 ? Math.round(line.original_line_price / line.quantity) : 0;
    var units = Math.min(t.quantity, ownUnits(cart, t) + line.quantity);
    return pool >= unit * units - 1;
  }
  // A gift line whose tier the island does not know (island missing the field).
  function giftFreeLoose(line) { return autoOff(line) >= line.original_line_price - 1; }

  // Per cart (keyed by cart token), per browser tab session (sessionStorage):
  //   added    the drawer put this tier's gift in the cart
  //   declined the shopper took it out (drawer Remove, or it vanished after we
  //            added it): never added again this session
  //   unpriced Shopify did not make it free under this set of codes (or a gift
  //            request failed): not added again until the codes change
  //   blocked  code set -> true: with these codes on the cart Shopify did not make
  //            a gift free, so the code does not combine with the gift discounts.
  //            While they are applied no gift step is shown and no tier is added
  //            (BEN10 and CHRIS10 block both tiers, release review 19 Oct 2026).
  var MEMO_KEY = 'pickld-c4-gifts';
  var memoMem = null;
  function memoFor(cart) {
    var m = null;
    try { m = JSON.parse(window.sessionStorage.getItem(MEMO_KEY) || 'null'); } catch (e) { m = memoMem; }
    if (!m || m.token !== cart.token) m = { token: cart.token, added: {}, declined: {}, unpriced: {}, blocked: {} };
    m.added = m.added || {}; m.declined = m.declined || {}; m.unpriced = m.unpriced || {}; m.blocked = m.blocked || {};
    return m;
  }
  function memoSave(m) {
    memoMem = m;
    try { window.sessionStorage.setItem(MEMO_KEY, JSON.stringify(m)); } catch (e) {}
  }
  function codeKey(cart) {
    return codes(cart).applied.map(function (c) { return String(c.code).toUpperCase(); }).sort().join(',');
  }
  function codeBlocksGifts(memo, cart) {
    var ck = codeKey(cart);
    return !!(ck && memo.blocked[ck]);
  }
  function tierBlocked(memo, t, cart) {
    return !!(memo.declined[t.handle] || memo.unpriced[t.handle + '|' + codeKey(cart)] || codeBlocksGifts(memo, cart));
  }
  // The sentence for a gift Shopify did not make free. With a code on the cart, the code is
  // what kept it from being free, so it is named; without one, the gift could not be added.
  function refusedNote(refused, cart) {
    var names = codes(cart).applied.map(function (c) { return String(c.code).toUpperCase(); });
    if (names.length) return names.join(' and ') + (names.length > 1 ? ' don’t' : ' doesn’t') + ' combine with the free gift.';
    return refused.length > 1 ? 'Free gifts could not be added to this cart.' : cap(refused[0].tier.label) + ' could not be added to this cart.';
  }

  // Setting stack_tiers. OFF by default: measured on staging 2026-10-18, Shopify
  // applies only ONE of two automatic Buy X Get Y discounts to a cart (the bigger
  // saving), so with both tiers reached the overgrips stayed at full price beside a
  // free Flick Stick. Off, the cart holds the gift of one tier only.
  function stacking() { return !!(state.meta && state.meta.stack_tiers === true); }

  // stack_tiers off: the ONE tier whose gift the cart should hold, or null. The
  // highest tier reached that can be given, checked from the top down:
  //   - a tier whose gift line is already in the cart keeps it (a shopper adding
  //     their own overgrips later must not lose the Flick Stick they have)
  //   - a higher gift is never ADDED while the shopper's own units of a lower tier
  //     are in the cart: Shopify would move its one discount to the higher gift and
  //     charge for their own overgrips, so the total would rise
  //   - a tier the shopper's own units already cover is the target with no line
  //   - otherwise the first tier in stock, not declined, not refused on these codes
  // A declined or unavailable higher gift falls back to the next tier down.
  function giftTarget(cart, ok, gf, memo) {
    if (!ok || !gf.parcel) return null;
    var list = giftTiers();
    var present = {};
    cart.items.forEach(function (it) { var h = giftOf(it); if (h) present[h] = true; });
    for (var i = list.length - 1; i >= 0; i--) {
      var t = list[i];
      if (gf.amount < t.threshold_cents) continue;
      if (present[t.handle]) return t;
      if (list.slice(0, i).some(function (u) { return ownUnits(cart, u) > 0; })) continue;
      if (ownUnits(cart, t) > 0 && giftNeed(cart, t) === 0) return t;
      if (t.available && !tierBlocked(memo, t, cart)) return t;
    }
    return null;
  }

  // Decide what the cart should hold, from the cart that exists now. Pure apart
  // from the memo it returns; nothing is sent from here.
  function planGifts(cart, opts) {
    opts = opts || {};
    var known = Array.isArray(state.meta && state.meta.gifts);
    var list = giftTiers();
    var ok = giftContextOk(cart);
    var gf = giftFigures(cart);
    var memo = memoFor(cart);
    var ck = codeKey(cart);
    var stack = stacking();
    var target = stack ? null : giftTarget(cart, ok, gf, memo);
    var plan = { updates: {}, add: [], memo: memo, events: [] };
    var kept = {};
    // One line per tier. When a tier has more than one (a gift line raised above the tier's
    // quantity, which Shopify splits into a CHARGED line listed first and a free one; or a
    // second colour added in another tab), the line kept is the one carrying the most gift
    // discount (ties: the first), never simply the first. The others go in the same update.
    var keeper = {};
    cart.items.forEach(function (line) {
      var h = giftOf(line);
      if (h && (!keeper[h] || autoOff(line) > autoOff(keeper[h]))) keeper[h] = line;
    });
    cart.items.forEach(function (line) {
      var h = giftOf(line);
      if (!h) return;
      var t = tierFor(h);
      var why = null;
      if (keeper[h] !== line) why = 'duplicate';
      else if (!t) why = known ? 'off' : (giftFreeLoose(line) ? null : 'unpriced');
      else if (tierVariants(t).indexOf(String(line.variant_id)) === -1) why = 'off';
      else if (!ok || !gf.parcel) why = 'context';
      else if (gf.amount < t.threshold_cents) why = 'under';
      // not stacking: any gift but the target's goes, before the free check, so a
      // lower gift Shopify no longer discounts is swapped, not refused
      else if (!stack && (!target || target.handle !== h)) why = 'superseded';
      else if (!giftFree(cart, line, t)) why = 'unpriced';
      if (why) {
        plan.updates[line.key] = 0;
        memo.added[h] = false;
        if (why === 'unpriced') memo.unpriced[h + '|' + ck] = true;
        if (why === 'unpriced' && t && ck) memo.blocked[ck] = true;
        plan.events.push({ why: why, tier: t, handle: h });
        return;
      }
      kept[h] = true;
      if (t && line.quantity > t.quantity) plan.updates[line.key] = t.quantity;
    });
    list.forEach(function (t) {
      if (kept[t.handle]) return;
      var touched = plan.events.some(function (e) { return e.handle === t.handle; });
      // We put this gift in the cart earlier in this session, it is gone, and we
      // did not take it off: the shopper removed it (here, on the cart page or in
      // another tab). Not added again this session.
      if (memo.added[t.handle] && !touched) { memo.declined[t.handle] = true; memo.added[t.handle] = false; }
      if (opts.allowAdd === false || !ok || !gf.parcel || touched) return;
      if (!stack && (!target || target.handle !== t.handle)) return;
      if (!t.available || gf.amount < t.threshold_cents || tierBlocked(memo, t, cart)) return;
      // the shopper's own units of these variants already take the free units
      var need = giftNeed(cart, t);
      if (need > 0) plan.add.push(Object.assign({}, t, { add_qty: need }));
    });
    return plan;
  }

  function planEmpty(plan) { return !Object.keys(plan.updates).length && !plan.add.length; }

  // Send the plan. Never rejects: a gift that cannot be added is logged and
  // suppressed for this code state, and the drawer settles on the cart that is.
  function applyGifts(plan) {
    var memo = plan.memo;
    var sections = null, last = null;
    begin();
    var p = Promise.resolve();
    if (Object.keys(plan.updates).length) {
      p = p.then(function () { return api(routes().update + '.js', { updates: plan.updates }); })
        .then(function (j) { last = j; sections = j.sections || sections; });
    }
    if (plan.add.length) {
      p = p.then(function () {
        return api(routes().add + '.js', { items: plan.add.map(function (t) {
          return { id: Number(t.variant_id), quantity: t.add_qty || t.quantity, properties: { _pk_gift: t.handle } };
        }) });
      }).then(function (j) {
        sections = j.sections || sections;
        plan.add.forEach(function (t) { memo.added[t.handle] = true; });
        return getCart();
      }).then(function (c) { last = c; });
    }
    return p.then(function () {
      memoSave(memo);
      // set before the settle render so the note shows on this render
      var refused = plan.events.filter(function (e) { return e.why === 'unpriced' && e.tier; });
      if (refused.length) state.giftNote = refusedNote(refused, state.cart);
      settle(last, sections);
      return true;
    }, function (err) {
      warn('gift ladder request failed; the gift is not added again until the codes change.', err && (err.description || err.message));
      var ck = state.cart ? codeKey(state.cart) : '';
      plan.add.forEach(function (t) { memo.unpriced[t.handle + '|' + ck] = true; memo.added[t.handle] = false; });
      memoSave(memo);
      return getCart().then(function (c) { settle(c, null); }, function () { settle(null, null); }).then(function () { return false; });
    });
  }

  function giftMessage(plan, skipAdd) {
    var parts = [];
    var added = plan.add.filter(function (t) { return !(skipAdd && skipAdd[t.handle]); });
    var swappedTo = null;
    var refused = [];
    plan.events.forEach(function (e) {
      if (!e.tier) return;
      if (e.why === 'under') parts.push('Removed ' + e.tier.label + ': the order is under ' + money(e.tier.threshold_cents) + '.');
      if (e.why === 'superseded') {
        // tiers not stacking: the lower gift makes way for the higher one
        if (added.length) { swappedTo = added[0]; parts.push('Swapped ' + e.tier.label + ' for ' + swappedTo.label + '.'); }
        else parts.push('Removed ' + e.tier.label + ': one free gift per order.');
      }
      if (e.why === 'unpriced') {
        refused.push(e);
        warn('gift ' + e.handle + ' was not free at Shopify (check the automatic discount and how it combines with ' + (codeKey(state.cart) || 'no code') + '); taken off.');
      }
    });
    if (refused.length) parts.push(refusedNote(refused, state.cart));
    added.forEach(function (t) {
      if (t === swappedTo) return;
      // a top-up beside the shopper's own units: the tier's label states the full quantity
      parts.push(t.add_qty && t.add_qty < t.quantity ? 'Topped up to ' + t.label + '.' : 'Added ' + t.label + '.');
    });
    return parts.join(' ');
  }

  // Shopify may report an automatic discount a moment after a line arrives. Before a
  // gift the drawer has JUST added is called not free, read the cart once more.
  function recheck(check, justAdded) {
    var refusedNew = check.events.some(function (e) { return e.why === 'unpriced' && justAdded[e.handle]; });
    if (!refusedNew) return Promise.resolve(check);
    begin();
    return new Promise(function (r) { setTimeout(r, 700); }).then(getCart).then(function (c) {
      settle(c, null);
      return planGifts(state.cart, { allowAdd: false });
    }, function () { settle(null, null); return check; });
  }

  // Bring the gift lines in line with the cart. Resolves with a sentence for the
  // live region ('' when nothing changed). When it added a gift, or folded a
  // tier's extra lines into the one it kept, a second step re-plans on the cart
  // Shopify returned with adding switched off, so a gift Shopify did not make free
  // comes straight back off (after one more read, for a gift just added). Two
  // steps at most, no recursion: this cannot loop.
  function reconcile(opts) {
    opts = opts || {};
    if (!state.cart) return Promise.resolve('');
    var plan = planGifts(state.cart, opts);
    if (planEmpty(plan)) { memoSave(plan.memo); return Promise.resolve(''); }
    return applyGifts(plan).then(function (sent) {
      if (!sent) return '';
      var folded = plan.events.some(function (e) { return e.why === 'duplicate'; });
      if (!plan.add.length && !folded) return giftMessage(plan);
      var justAdded = {};
      plan.add.forEach(function (t) { justAdded[t.handle] = true; });
      return recheck(planGifts(state.cart, { allowAdd: false }), justAdded).then(function (check) {
        if (planEmpty(check)) { memoSave(check.memo); return giftMessage(plan); }
        return applyGifts(check).then(function (sent2) {
          var gone = {};
          if (sent2) check.events.forEach(function (e) { gone[e.handle] = true; });
          return [giftMessage(plan, gone), sent2 ? giftMessage(check) : ''].filter(Boolean).join(' ');
        });
      });
    });
  }

  // On page load, only when the cart already holds a gift line: take off a gift
  // that no longer qualifies (a change made on the cart page, a tier switched
  // off) before anyone checks out with it. Never adds. On the cart page
  // (sections/main-pickld-cart.liquid), whose form posts quantities by position:
  // while the check runs the html element carries data-c4-checking, which holds
  // that form's submits, and when the check changed the cart the page reloads so
  // it shows the cart that exists. Every time it changes, guarded against a loop.
  function loadCheck() {
    var onCartPage = !!(state.meta && state.meta.page === 'cart');
    var lines = (state.meta && state.meta.lines) || {};
    var has = Object.keys(lines).some(function (k) { return lines[k] && lines[k].gift; });
    if (!has) { if (onCartPage) reloadReset(); return; }
    var hold = function (on) { if (onCartPage) document.documentElement.toggleAttribute('data-c4-checking', on); };
    hold(true);
    getCart().then(function (c) {
      state.cart = c;
      render();
      var sig = function () { return JSON.stringify(((state.cart && state.cart.items) || []).map(function (i) { return [i.key, i.quantity]; })); };
      var before = sig();
      return reconcile({ allowAdd: false }).then(function () {
        if (!onCartPage) return;
        // the hold stays on while the page reloads (or when the guard stops it: a stale form must not post)
        if (sig() !== before) reloadToMatch(); else { reloadReset(); hold(false); }
      });
    }).catch(function (e) { warn('page-load gift check failed', e && e.message); hold(false); });
  }

  // The loop guard: at most two reloads in a row that each changed the cart again. A load
  // whose check changes nothing resets it. Each check only ever removes or trims gift lines,
  // so a third change in a row would be a bug: it is logged and the page is left as it is.
  // Without sessionStorage there is nothing to count with, so it reloads: the check only
  // removes gift lines, so it settles within a load or two.
  var RELOAD_KEY = 'pickld-c4-reloads';
  function reloadReset() { try { window.sessionStorage.removeItem(RELOAD_KEY); } catch (e) {} }
  function reloadToMatch(go) {
    var n = 0;
    try { n = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0); window.sessionStorage.setItem(RELOAD_KEY, String(n + 1)); } catch (e) { n = 0; }
    if (n >= 2) { warn('the cart page check changed the cart on three loads in a row; not reloading again.'); return false; }
    (go || function () { window.location.reload(); })();
    return true;
  }

  /* ---------- focus ---------- */

  // innerHTML re-renders destroy the focused control. Record where the shopper
  // was (by line key, offer tag or code control), put focus back after render.
  function focusKeyOf(node) {
    if (!node || !node.closest || !root.contains(node)) return null;
    var line = node.closest('[data-key]');
    var lines = Array.prototype.slice.call(root.querySelectorAll('[data-key]'));
    var idx = line ? lines.indexOf(line) : -1;
    if (node.closest('[data-c4-qty]')) return { k: 'qty', key: line && line.dataset.key, dir: node.closest('[data-c4-qty]').dataset.c4Qty, idx: idx };
    if (node.closest('[data-c4-remove]')) return { k: 'rm', key: line && line.dataset.key, idx: idx };
    var row = node.closest('[data-offer-row]');
    if (row) {
      var opt = node.closest('[data-c4-opt]');
      if (opt) return { k: 'opt', tag: row.dataset.offerRow, i: Number(opt.dataset.c4Opt) };
      if (node.closest('[data-c4-choose]')) return { k: 'choose', tag: row.dataset.offerRow };
      if (node.closest('.c4__ch')) return { k: 'chooser', tag: row.dataset.offerRow };
      return { k: 'offer', tag: row.dataset.offerRow };
    }
    if (node.closest('[data-c4-code]')) return { k: 'code' };
    return null;
  }

  function restoreFocus(fk) {
    if (!fk || !root.classList.contains('is-open')) return;
    var t = null;
    var lines = root.querySelectorAll('[data-key]');
    function lineBtn(i) {
      var l = lines[Math.min(i, lines.length - 1)];
      return l ? (l.querySelector('[data-c4-qty="-1"]') || l.querySelector('[data-c4-remove]')) : null;
    }
    if (fk.k === 'qty' || fk.k === 'rm') {
      var same = fk.key ? root.querySelector('[data-key="' + (window.CSS && CSS.escape ? CSS.escape(fk.key) : fk.key) + '"]') : null;
      if (same) t = fk.k === 'qty' ? same.querySelector('[data-c4-qty="' + fk.dir + '"]') : same.querySelector('[data-c4-remove]');
      else t = lineBtn(Math.max(0, fk.idx));
      // + shut at the stock limit cannot take focus: the line's - (or Remove) does
      if (t && t.disabled) t = t.closest('[data-key]').querySelector('[data-c4-qty="-1"]') || t.closest('[data-key]').querySelector('[data-c4-remove]');
    } else if (fk.k === 'offer') {
      // the row's own Add (or Swap) first: the chooser's toggle is a button too
      var r = root.querySelector('[data-offer-row="' + fk.tag + '"] [data-c4-add], [data-offer-row="' + fk.tag + '"] [data-c4-swap]') ||
        root.querySelector('[data-offer-row="' + fk.tag + '"] button');
      t = r || root.querySelector('[data-offer-row] [data-c4-add], [data-offer-row] [data-c4-swap]') || root.querySelector('[data-offer-row] button') || lineBtn(0);
    } else if (fk.k === 'opt' || fk.k === 'chooser' || fk.k === 'choose') {
      var orow = offerRowEl(fk.tag);
      if (orow && fk.k === 'choose') t = orow.querySelector('[data-c4-choose]');
      if (orow && fk.k === 'opt') t = optControl(orow, fk.i);
      if (orow && !t) t = chooserFocusTarget(orow) || orow.querySelector('[data-c4-choose]') || orow.querySelector('[data-c4-add]');
    } else if (fk.k === 'code') {
      t = root.querySelector('#c4-code-input') || root.querySelector('[data-c4-keep]') || root.querySelector('.c4__chip-c') || root.querySelector('[data-c4-code-remove]') || root.querySelector('[data-c4-code-toggle]');
    }
    t = t || el.close;
    if (t && t.focus) t.focus({ preventScroll: false });
  }

  // The control for option i in a chooser: its select, or the checked swatch
  // (else the first open one), as a keyboard user would land on it.
  function optControl(row, i) {
    var ctl = Array.prototype.slice.call(row.querySelectorAll('[data-c4-opt="' + i + '"]'));
    if (!ctl.length) return null;
    if (ctl[0].tagName === 'SELECT') return ctl[0];
    return ctl.filter(function (n) { return n.checked && !n.disabled; })[0] || ctl.filter(function (n) { return !n.disabled; })[0] || null;
  }

  // Where focus goes when a chooser opens: its first control, else its message.
  function chooserFocusTarget(row) {
    var ch = row.querySelector('.c4__ch');
    if (!ch) return null;
    var first = ch.querySelector('[data-c4-opt]');
    return (first && optControl(row, first.dataset.c4Opt)) || ch.querySelector('a[href]') || ch;
  }

  /* ---------- render ---------- */

  function render() {
    var cart = state.cart;
    if (!cart) return;
    var fk = state.focusKey || focusKeyOf(document.activeElement);
    state.focusKey = null;
    var count = cart.item_count;
    el.count.textContent = count;
    document.querySelectorAll('[data-pickld-cart-count-global]').forEach(function (n) { n.textContent = count; });
    root.classList.toggle('is-empty', count === 0);

    if (count === 0) {
      el.ship.innerHTML = '';
      el.foot.hidden = true;
      if (el.pin) { el.pin.innerHTML = ''; el.pin.hidden = true; }
      el.body.innerHTML = renderEmpty();
      if (fk) restoreFocus({ k: 'none' });
      return;
    }
    el.foot.hidden = false;
    var f = figures(cart);
    el.ship.innerHTML = renderShip(f);
    var offers = liveOffers();
    // a chooser whose row is gone (the offers moved on) is closed, not kept hidden
    if (state.chooser && !offers.some(function (o) { return chooserOpenFor(o) && canChoose(o); })) state.chooser = null;
    var pin = pinnedOffer(offers);
    if (el.pin) { el.pin.innerHTML = pin ? pinHtml(pin) : ''; el.pin.hidden = !pin; }
    var offersHtml = renderOffers(f, pin ? offers.slice(1) : offers, !!pin);
    el.body.innerHTML = renderLines(cart) + (offersHtml || '') + renderNext(cart, f);
    el.code.innerHTML = renderCode(cart, f);
    el.totals.innerHTML = renderTotals(cart, f);
    renderCheckout(f);
    var m = state.meta || {};
    // Wallets are hidden only for a deposit line (stock-rule pre-orders check out like any order).
    if (el.express) el.express.hidden = m.express_ok === false || (f.hasDeposit && m.express_hide_with_preorder !== false);
    if (el.trust && m.trust) el.trust.textContent = m.trust;
    if (fk) restoreFocus(fk);
  }

  function renderSkeleton() {
    var n = Math.max(1, Math.min(3, Object.keys((state.meta && state.meta.lines) || {}).length || 1));
    var rows = '';
    for (var i = 0; i < n; i++) rows += '<li class="c4__skel"><i></i><span><b></b><b></b></span></li>';
    el.body.innerHTML = '<ul class="c4__lines" role="list" aria-busy="true" aria-label="Loading your cart">' + rows + '</ul>';
  }

  function renderEmpty() {
    var m = state.meta || {};
    var links = (m.empty_links || []).map(function (l) {
      return '<li><a class="c4__chiplink" href="' + esc(l.url) + '">' + esc(l.label) + '</a></li>';
    }).join('');
    return '<div class="c4__empty">' +
      '<p class="c4__empty-h">Your cart is <em>empty</em>.</p>' +
      '<p class="c4__empty-p">Start with a paddle, or top up balls and grips.</p>' +
      (links ? '<ul class="c4__chips" role="list">' + links + '</ul>' : '') +
      '</div>';
  }

  function renderShip(f) {
    var html = shipStatus(f);
    var d = dispatchText(state.cart);
    if (d) html += '<p class="c4__ship-sub c4__dispatch">' + esc(d) + '</p>';
    var gn = state.giftNote || codeNote(state.cart);
    if (gn) html += '<p class="c4__ship-sub c4__warn">' + esc(gn) + '</p>';
    return html;
  }

  // While a code that kept a gift from being free is on the cart, every gift step is hidden
  // (tierBlocked), so the band says why for as long as the cart would otherwise earn a gift,
  // not only on the render where the gift came off.
  function codeNote(cart) {
    var list = giftTiers();
    if (!list.length || !giftContextOk(cart) || !codeBlocksGifts(memoFor(cart), cart)) return '';
    var gf = giftFigures(cart);
    return gf.parcel && gf.amount >= list[0].threshold_cents ? refusedNote([], cart) : '';
  }

  function shipRow(tone, icon, text) {
    return '<div class="c4__ship-row"><span class="c4__ic c4__ic--' + tone + '" aria-hidden="true">' + icon + '</span><p>' + text + '</p></div>';
  }

  function bar(value, at, what) {
    var now = Math.max(0, Math.min(value, at));
    var pct = at > 0 ? Math.min(100, Math.round(now / at * 100)) : 100;
    return '<div class="c4__bar" role="progressbar" aria-label="' + esc('Progress to ' + what) + '" aria-valuemin="0" aria-valuemax="' + at + '" aria-valuenow="' + now + '" aria-valuetext="' + esc(money(now) + ' of ' + money(at)) + '"><i style="width:' + pct + '%"></i></div>';
  }

  function shipStatus(f) {
    var cart = state.cart;
    // The threshold is AUD and ship-to-Australia; any other currency or
    // storefront country gets no bar rather than a wrong one.
    if (promiseOk(cart) && f.onlyBulky) {
      return shipRow('n', '!', 'Bulky items ship at a freight rate, shown at checkout.');
    }
    if (promiseOk(cart) && f.hasDeposit && !f.splitShown) {
      // Not proven whether Shopify judges free shipping on the deposit or the full value.
      return shipRow('n', van(), 'Shipping for a pre-order is confirmed at checkout.');
    }
    var steps = f.steps;
    if (!steps.length) return '';
    var next = null;
    for (var i = 0; i < steps.length; i++) { if (!steps[i].met) { next = steps[i]; break; } }
    var ship = steps.filter(function (s) { return s.kind === 'ship'; })[0];
    var giftSteps = steps.some(function (s) { return s.kind === 'gift'; });
    var except = f.hasBulky ? ' (except bulky items)' : '';
    var bulkyNote = !f.hasBulky ? '' : '<p class="c4__ship-sub">' + (giftSteps
      ? 'Bulky items are not part of free shipping and ship at a freight rate.'
      : 'Bulky items are not included and ship at a freight rate.') + '</p>';
    if (!next) {
      var last = steps[steps.length - 1];
      var have = f.giftLines === 1 ? ' Your free gift is in the cart.' : f.giftLines > 1 ? ' Your free gifts are in the cart.' : '';
      var text = (ship ? '<b>Free AU shipping</b> on this order' + except + '.' : '') + have;
      return shipRow('g', tick(), text.trim()) + bar(last.at, last.at, last.kind === 'ship' ? 'free AU shipping' : last.tier.label) + bulkyNote;
    }
    var gap = next.at - next.value;
    var shipDone = ship && ship.met;
    // One line on a phone: once the next step is a gift, free shipping is already
    // in hand and the total says so ("GST included. Free AU shipping."), so the
    // band names only the gift. A cart with bulky items keeps the longer wording,
    // because there the total reads "Shipping at checkout".
    // Tiers not stacking: the next gift REPLACES a lower gift already in the cart,
    // so the line says "instead" and never reads as a promise of both.
    var replaces = next.kind === 'gift' && !stacking() && state.cart.items.some(function (it) {
      var tt = tierFor(giftOf(it));
      return tt && tt.threshold_cents < next.at;
    });
    var text2 = next.kind === 'ship'
      ? '<b>' + money(gap) + '</b> from free AU shipping.'
      : (shipDone && f.hasBulky ? 'Free AU shipping' + except + '. ' : '') + '<b>' + money(gap) + '</b> from ' + esc(next.tier.label) + (replaces ? ' instead.' : '.');
    var lost = next.before >= next.at && next.value < next.at && f.saved > 0;
    var warnLine = !lost ? '' : '<p class="c4__ship-sub c4__warn">Your code takes the order under ' + money(next.at) +
      (next.kind === 'ship' ? ', the free shipping line is judged after the code.' : ', and gifts are judged after the code.') + '</p>';
    return shipRow('n', next.kind === 'gift' ? giftIcon() : van(), text2) +
      bar(next.value, next.at, next.kind === 'ship' ? 'free AU shipping' : next.tier.label) + warnLine + bulkyNote;
  }

  // "Order by 12pm Mon to Fri, ships same day" (setting dispatch_line) is shown
  // only when it is true for the whole cart: no pre-order line (dispatch holds a
  // deposit order whole until every line lands, and splits a paid order only for
  // a paddle or bag, scripts/dispatch_triage.py in pickld-growth), and at least
  // one line that ships from our shelf. Lines that ship from a supplier carry
  // their own wording (custom.dispatch_eta); a line the island does not know yet
  // suppresses the claim.
  function dispatchText(cart) {
    var m = state.meta || {};
    var d = String(m.dispatch_line || '').trim();
    if (!d || !cart) return '';
    var lines = cart.items.filter(function (it) { return !giftOf(it) && !it.gift_card && it.requires_shipping !== false; });
    if (!lines.length) return '';
    if (lines.some(function (it) { return !inIsland(it) || lineMeta(it).preorder; })) return '';
    return lines.some(function (it) { return !lineMeta(it).supplier; }) ? d : '';
  }

  function renderLines(cart) {
    var mine = cart.items.filter(function (it) { return !giftOf(it); });
    var gifts = cart.items.filter(giftOf).sort(function (a, b) {
      var ta = tierFor(giftOf(a)), tb = tierFor(giftOf(b));
      return (ta ? ta.threshold_cents : 1e12) - (tb ? tb.threshold_cents : 1e12);
    });
    return '<ul class="c4__lines" role="list">' + mine.map(lineHtml).join('') + gifts.map(giftLineHtml).join('') + '</ul>';
  }

  function thumb(item) {
    return '<a class="c4__th" href="' + esc(item.url) + '" tabindex="-1" aria-hidden="true"><img src="' + esc(img(item.featured_image && item.featured_image.url || item.image, 160)) + '" alt="" loading="lazy" width="72" height="72"></a>';
  }

  function lineHtml(item) {
    var lm = lineMeta(item);
    var title = nice(item.product_title);
    var variant = item.product_has_only_default_variant ? '' : '<p class="c4__vt">' + esc(item.variant_title) + '</p>';
    var lineDisc = item.line_level_total_discount > 0;
    var price = lineDisc
      ? '<s>' + money(item.original_line_price) + '</s> ' + money(item.final_line_price)
      : money(item.final_line_price);
    var pre = '';
    if (lm.preorder) {
      // Same facts as the old drawer since dc9adf5: a deposit is due today and the
      // balance when it ships (the amount only when it checked out); a stock-rule
      // pre-order ships with the next shipment, the PDP's wording.
      var said = isDeposit(item)
        ? (lm.deposit != null ? money(lm.deposit * item.quantity) + ' deposit due today, balance charged when it ships.' : 'Deposit due today, balance charged when it ships.')
        : 'Ships when the next shipment lands.';
      pre = '<p class="c4__pre"><span class="c4__pill">Pre-order</span><span>' + said + '</span></p>' +
        (lm.eta ? '<p class="c4__eta">' + esc(lm.eta) + '</p>' : '');
    } else if (lm.supplier && lm.eta) {
      // shipped by the supplier: its own dispatch wording, the same field the PDP prints
      pre = '<p class="c4__eta">' + esc(lm.eta) + '</p>';
    }
    var bulky = lm.bulky ? '<p class="c4__pre"><span class="c4__pill c4__pill--amb">Bulky</span><span>Freight at checkout, Australia only.</span></p>' : '';
    var limited = lm.limited ? '<p class="c4__ltd">' + esc(lm.limited_label ? LIMITED.line : LIMITED.lineOther) + '</p>' : '';
    // + is shut once Shopify has refused more of this line (stock or a quantity rule), counted
    // across a line Shopify split in two; opening the drawer again re-reads and re-opens it.
    var limit = state.limits[lineId(item)];
    var atLimit = limit != null && sum(state.cart.items.filter(function (i) { return sameLine(i, item); }), function (i) { return i.quantity; }) >= limit;
    var more = atLimit
      ? '<button type="button" data-c4-qty="1" disabled aria-label="' + esc('No more ' + title + ' can be added') + '">' + plus() + '</button>'
      : '<button type="button" data-c4-qty="1" aria-label="One more ' + esc(title) + '">' + plus() + '</button>';
    return '<li class="c4__line" data-key="' + esc(item.key) + '">' + thumb(item) +
      '<div class="c4__lb">' +
        '<div class="c4__lt"><a href="' + esc(item.url) + '" class="c4__ln">' + esc(title) + '</a><span class="c4__lp">' + price + '</span></div>' +
        variant + limited + pre + bulky +
        '<div class="c4__lr">' +
          '<div class="c4__qty" role="group" aria-label="Quantity, ' + esc(title) + '"><button type="button" data-c4-qty="-1" aria-label="One fewer ' + esc(title) + '">' + minus() + '</button>' +
          '<span>' + item.quantity + '</span>' + more + '</div>' +
          '<button type="button" class="c4__rm" data-c4-remove aria-label="Remove ' + esc(title) + '">Remove</button>' +
        '</div>' +
      '</div></li>';
  }

  // A gift line: no quantity stepper (the tier sets the quantity), "Free gift"
  // only once the cart shows it is free, and a Remove that declines it for the
  // session. While Shopify has not made it free it shows its real price and no
  // claim; the ladder takes it off on the same pass.
  function giftLineHtml(item) {
    var t = tierFor(giftOf(item));
    var title = nice(item.product_title);
    var free = t ? giftFree(state.cart, item, t) : giftFreeLoose(item);
    var variant = item.product_has_only_default_variant ? '' : '<p class="c4__vt">' + esc(item.variant_title) + '</p>';
    var price = free ? '<s>' + money(item.original_line_price) + '</s> Free' : money(item.final_line_price);
    var why = free ? '<p class="c4__pre"><span class="c4__pill c4__pill--gift">Free gift</span>' +
      (t ? '<span>With orders of ' + money(t.threshold_cents) + ' or more.</span>' : '') + '</p>' : '';
    return '<li class="c4__line c4__line--gift" data-key="' + esc(item.key) + '" data-gift="' + esc(giftOf(item)) + '">' + thumb(item) +
      '<div class="c4__lb">' +
        '<div class="c4__lt"><a href="' + esc(item.url) + '" class="c4__ln">' + esc(title) + '</a><span class="c4__lp">' + price + '</span></div>' +
        variant + why +
        '<div class="c4__lr">' +
          '<span class="c4__gq">Qty ' + item.quantity + '</span>' +
          '<button type="button" class="c4__rm" data-c4-remove aria-label="' + esc('Remove free gift, ' + title) + '">Remove</button>' +
        '</div>' +
      '</div></li>';
  }

  // What an offer row prints for its variant. A bare value read as noise beside the price
  // ("Lead Tape Strips, 3g $5.00 2", "12 Pack (Optic Yellow) 12"; release review, 19 Oct): a
  // value the title already says is dropped, and the one option of a product whose option is
  // not a colour is named ("Bundle: 2") when the offer carries option_names. Several options
  // ("Clear / Black x3") and colours ("Pink") read as Shopify writes them.
  function variantLabel(o, title, vTitle) {
    var v = String(vTitle || '').trim();
    if (!v || v.indexOf(' / ') > -1) return v;
    if (saysAlready(title, v)) return '';
    var names = Array.isArray(o.option_names) ? o.option_names : [];
    return names.length === 1 && names[0] && !isColourOption(names[0]) ? names[0] + ': ' + v : v;
  }
  // The value as a whole word in the title, any case ("12" in "12 Pack", not in "120").
  function saysAlready(title, value) {
    var t = String(title).toLowerCase(), v = value.toLowerCase();
    for (var at = t.indexOf(v); at > -1; at = t.indexOf(v, at + 1)) {
      if (!/[a-z0-9]/.test(t.charAt(at - 1)) && !/[a-z0-9]/.test(t.charAt(at + v.length))) return true;
    }
    return false;
  }

  function liveOffers() {
    return ((state.meta && state.meta.offers) || []).filter(function (o) { return o.kind !== 'swap' || o.delta > 0; });
  }

  // Test variant (setting pinned_offer, off by default): on a phone, with two or
  // more lines in the cart, the first offer sits in a slim row above the code
  // line, so it stays on screen while the lines push the offers below the fold.
  function pinnedOffer(offers) {
    var m = state.meta || {};
    if (m.pinned_offer !== true || !phoneQuery || !phoneQuery.matches) return null;
    if (!state.cart || state.cart.items.length < 2 || !offers.length) return null;
    return offers[0];
  }

  function pinHtml(o) {
    var isSwap = o.kind === 'swap';
    var title = nice(o.title);
    var label = variantLabel(o, title, o.variant_title);
    var full = title + (label ? ', ' + label : '');
    var image = o.image ? '<img src="' + esc(img(o.image, 96)) + '" alt="" loading="lazy" width="36" height="36">' : '';
    var btn = isSwap
      ? '<button type="button" class="c4__add c4__add--swap" data-c4-swap data-from="' + o.swap_from + '" data-to="' + o.variant_id + '" aria-label="' + esc('Swap ' + nice(o.swap_from_title || 'your pack') + ' for ' + title + ', plus ' + money(o.delta)) + '">Swap</button>'
      : '<button type="button" class="c4__add" data-c4-add data-pin="1" data-variant="' + o.variant_id + '" data-offer="' + esc(o.offer) + '"' + (o.explore ? ' data-explore="1"' : '') + ' aria-label="' + esc('Add ' + full + ', ' + money(o.price)) + '">+ Add</button>';
    return '<div class="c4__pinrow" data-offer-row="' + esc(o.offer) + '">' +
      '<a class="c4__pin-i" href="' + esc(o.url) + '" tabindex="-1" aria-hidden="true">' + image + '</a>' +
      '<p class="c4__pin-t"><a href="' + esc(o.url) + '">' + esc(full) + '</a><span>' + (isSwap ? '+' + money(o.delta) : money(o.price)) + '</span></p>' +
      btn + '</div>';
  }

  function renderOffers(f, offers, plain) {
    if (!offers.length) return '';
    var heading = (state.meta.offer_heading || 'Pair with');
    var first = offers[0];
    var rest = plain ? offers : offers.slice(1);
    var cur = first.eyebrow || heading;
    var out = '<section class="c4__offers" aria-label="' + esc(cur) + '">';
    out += '<p class="c4__oh">' + headingHtml(cur) + '</p>';
    if (!plain) out += offerCard(first, true, f);
    if (rest.length) {
      // A row whose reason differs from the one above gets its own eyebrow,
      // so a ball swap never sits under "One add from free shipping".
      out += '<ul class="c4__orows" role="list">';
      rest.forEach(function (o) {
        var eb = o.eyebrow || heading;
        if (eb !== cur) {
          out += '</ul><p class="c4__oh c4__oh--sub">' + headingHtml(eb) + '</p><ul class="c4__orows" role="list">';
          cur = eb;
        }
        out += '<li>' + offerCard(o, false, f) + '</li>';
      });
      out += '</ul>';
    }
    return out + '</section>';
  }

  // Pre-order carts: say what happens next, under the offers when there are any.
  function renderNext(cart, f) {
    if (!f.hasPre) return '';
    var deposit = f.hasDeposit
      ? '<li><b>Today:</b> the ' + (f.depositsKnown ? money(f.deposits) + ' ' : '') + 'deposit' + (cart.items.length > 1 ? ', plus your in-stock items' : '') + '.</li>' +
        '<li><b>When it ships:</b> ' + (f.splitShown ? 'the balance of ' + money(f.later) + ' is charged.' : 'the balance is charged.') + '</li>'
      : '<li><b>Today:</b> paid in full. It ships when stock lands.</li>';
    return '<section class="c4__next" aria-label="What happens next"><p class="c4__oh">What happens <em>next</em></p><ol>' +
      deposit + '<li>Checkout shows the exact amounts before you pay.</li></ol></section>';
  }

  function headingHtml(t) {
    // The one serif accent: the last word of the heading, "Pair with your *paddle*".
    var words = esc(t).split(' ');
    if (words.length < 2) return words.join(' ');
    var last = words.pop();
    return words.join(' ') + ' <em>' + last + '</em>';
  }

  function offerCard(o, featured, f) {
    var isSwap = o.kind === 'swap';
    var title = nice(o.title);
    // The row shows the variant the shopper picked in its chooser, else the offer's.
    var choose = !isSwap && canChoose(o);
    var open = choose && chooserOpenFor(o);
    var v = choose ? chosenVariant(o) : null;
    var vId = v ? v.id : o.variant_id;
    var vTitle = v ? v.title : o.variant_title;
    var vPrice = v ? v.price : o.price;
    var vCompare = v ? (v.compare_at_price || 0) : o.compare;
    var vImage = v ? variantImage(o, v) : o.image;
    var url = v ? String(o.url || '').replace(/([?&]variant=)\d+/, '$1' + v.id) : o.url;
    var vLabel = variantLabel(o, title, vTitle);
    var full = title + (vLabel ? ', ' + vLabel : '');
    // The chooser's data is fresher than the island: if it says the selected
    // variant cannot be had today, the row says so instead of offering it.
    var blocked = false;
    if (open && state.chooser.status === 'ready') {
      var pd = productData[handleOf(o)];
      blocked = !choosable(o, variantOf(pd, selOf(o, pd)));
    }
    var btn = isSwap
      ? '<button type="button" class="c4__add c4__add--swap" data-c4-swap data-from="' + o.swap_from + '" data-to="' + o.variant_id + '" aria-label="' + esc('Swap ' + nice(o.swap_from_title || 'your pack') + ' for ' + title + ', plus ' + money(o.delta)) + '">Swap, +' + money(o.delta) + '</button>'
      : blocked
        ? '<button type="button" class="c4__add" disabled aria-label="' + esc(full + ', sold out') + '">Sold out</button>'
        : '<button type="button" class="c4__add" data-c4-add data-variant="' + vId + '" data-offer="' + esc(o.offer) + '"' + (o.explore ? ' data-explore="1"' : '') + ' aria-label="' + esc('Add ' + full + ', ' + money(vPrice)) + '">+ Add</button>';
    var variant = vLabel ? '<span class="c4__ov">' + esc(vLabel) + '</span>' : '';
    var label = o.options_label || 'Other options';
    var other = !o.has_options ? '' : choose
      ? '<button type="button" class="c4__ovl" data-c4-choose aria-expanded="' + (open ? 'true' : 'false') + '"' +
        (open ? ' aria-controls="' + chooserId(o) + '" aria-label="' + esc('Done, close the options for ' + title) + '">Done' : ' aria-label="' + esc(label + ', ' + title) + '">' + esc(label)) + '</button>'
      : '<a class="c4__ovl" href="' + esc(o.url) + '" aria-label="' + esc(label + ', ' + title) + '">' + esc(label) + '</a>';
    var badge = o.badge ? '<span class="c4__badge">' + esc(o.badge) + '</span>' : '';
    var noteText = o.note;
    if (v && v.price !== o.price) {
      // The Liquid's line was computed for the offer's price; a variant at another
      // price keeps only what can still be said: what it adds after the code.
      noteText = o.trigger === 'gap' && o.price > 0 ? 'Adds ' + money(Math.round(v.price * o.price_after / o.price)) + '.' : '';
    } else if (o.trigger === 'gap' && f && f.gap != null && o.price_after < f.gap) {
      // The Liquid judged "clears free shipping" against the cart it saw; if the
      // cart has moved since (a code applied client side), drop the claim.
      noteText = 'Adds ' + money(o.price_after) + '.';
    }
    var note = noteText ? '<p class="c4__on">' + esc(noteText) + '</p>' : '';
    var image = vImage ? '<img src="' + esc(img(vImage, featured ? 240 : 140)) + '" alt="" loading="lazy">' : '';
    return '<div class="c4__offer' + (featured ? ' c4__offer--feat' : '') + (open ? ' c4__offer--open' : '') + '" data-offer-row="' + esc(o.offer) + '">' +
      '<a class="c4__oi" href="' + esc(url) + '" tabindex="-1" aria-hidden="true">' + image + '</a>' +
      '<div class="c4__ob">' +
        '<p class="c4__ot">' + badge + '<a href="' + esc(url) + '">' + esc(title) + '</a></p>' +
        '<p class="c4__op">' + (isSwap ? '' : money(vPrice)) + (vCompare > vPrice ? ' <s>' + money(vCompare) + '</s>' : '') + variant + '</p>' +
        note + other +
      '</div>' + (open ? chooserHtml(o, title) : '') + btn + '</div>';
  }

  /* ---------- inline variant chooser (offer rows) ---------- */

  // "Other colours" / "Other options" on an ADD row opens the product's options
  // inside the row (Ben, 18 Oct: pick the variant without leaving the cart).
  // docs/components/cart-c4.md, 7.1. The default render is unchanged: variants
  // are read from /products/<handle>.js only when a chooser first opens, and
  // kept for the page. A variant can be chosen only if the drawer could offer
  // it today: available, no selling plan required, and not on the offer's
  // not_today list (pre-orders, from snippets/pickld-preorder-ids.liquid via the
  // offer JSON, because the product JSON carries no stock). Swap rows keep their
  // product link: another pack would change the swap's price difference.
  var productCache = {};   // handle -> Promise of the product JSON
  var productData = {};    // handle -> the product JSON once it has loaded

  function handleOf(o) {
    if (o.handle) return o.handle;
    var m = /\/products\/([^\/?#]+)/.exec(o.url || '');
    return m ? decodeURIComponent(m[1]) : null;
  }

  // Fail closed: an island without not_today (an older render) keeps the link.
  function canChoose(o) {
    return !!o && o.kind !== 'swap' && o.has_options === true && Array.isArray(o.not_today) && !!handleOf(o);
  }

  function offerByTag(tag) {
    return ((state.meta && state.meta.offers) || []).filter(function (o) { return o.offer === tag; })[0] || null;
  }

  function chooserOpenFor(o) {
    return !!state.chooser && state.chooser.tag === o.offer && state.chooser.pid === o.product_id;
  }

  function chooserId(o) { return 'c4-ch-' + String(o.offer).replace(/[^A-Za-z0-9_-]/g, '-'); }

  function loadProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(routes().root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(parse)
        .then(function (p) {
          if (!p || !Array.isArray(p.variants) || !p.variants.length) throw new Error('No variants');
          productData[handle] = p;
          return p;
        })
        .catch(function (err) { delete productCache[handle]; throw err; });   // the next open tries again
    }
    return productCache[handle];
  }

  // /products/<handle>.js gives options as [{name, position, values}] (older
  // payloads give names only); values missing are read from the variants.
  function optionsOf(p) {
    return (p.options || []).map(function (op, i) {
      var name = typeof op === 'string' ? op : (op && op.name) || '';
      var values = op && Array.isArray(op.values) ? op.values.slice() : [];
      if (!values.length) p.variants.forEach(function (v) { var x = (v.options || [])[i]; if (x != null && values.indexOf(x) < 0) values.push(x); });
      return { name: name, values: values };
    });
  }

  function choosable(o, v) {
    if (!v || v.available !== true || v.requires_selling_plan) return false;
    var nt = (o.not_today || []).map(Number);
    return nt.indexOf(Number(v.id)) < 0;
  }

  function variantOf(p, sel) {
    return p.variants.filter(function (v) { return sel.every(function (x, k) { return (v.options || [])[k] === x; }); })[0] || null;
  }

  // The variant this row shows: the shopper's pick, while it can still be had.
  function chosenVariant(o) {
    var c = state.choices[o.offer];
    var p = productData[handleOf(o)];
    if (!c || !p || c.pid !== o.product_id || c.vid === o.variant_id) return null;
    var v = p.variants.filter(function (x) { return x.id === c.vid; })[0];
    return v && choosable(o, v) ? v : null;
  }

  function selOf(o, p) {
    var v = chosenVariant(o) || p.variants.filter(function (x) { return x.id === o.variant_id; })[0] || p.variants[0];
    return (v.options || []).slice();
  }

  // An image for the picked variant: its own, else the product's (never the
  // offer variant's, which would show the wrong colour).
  function variantImage(o, v) {
    var im = v.featured_image && (v.featured_image.src || v.featured_image);
    var p = productData[handleOf(o)];
    return im || (p && p.featured_image) || o.image;
  }

  // Shut values are worked out in option order: a value of option N is open
  // only if a variant that can be had today matches options 1 to N-1 as chosen
  // and carries it, so every open value leads somewhere addable (later options
  // move to the closest match, see settleSel). A shut value says why.
  function valueState(o, p, sel, i, value) {
    var hits = p.variants.filter(function (v) {
      var vo = v.options || [];
      for (var k = 0; k < i; k++) if (vo[k] !== sel[k]) return false;
      return vo[i] === value;
    });
    if (hits.some(function (v) { return choosable(o, v); })) return 'ok';
    return hits.some(function (v) { return v.available === true; }) ? 'pre' : 'sold';
  }

  // After option i changes: keep the rest if that exact variant can be had,
  // else the variant that can, matching options 1 to i, keeping most of the rest.
  function settleSel(o, p, sel, i) {
    if (choosable(o, variantOf(p, sel))) return sel;
    var best = null, score = -1;
    p.variants.forEach(function (v) {
      var vo = v.options || [];
      if (!choosable(o, v)) return;
      for (var k = 0; k <= i; k++) if (vo[k] !== sel[k]) return;
      var keep = 0;
      for (k = i + 1; k < sel.length; k++) if (vo[k] === sel[k]) keep++;
      if (keep > score) { best = v; score = keep; }
    });
    return best ? best.options.slice(0, sel.length) : sel;
  }

  // Mirror of snippets/pickld-colour-swatch.liquid, which the product page runs
  // for options whose name says colour or color. Its path (a), Shopify's native
  // swatch, is not in /products/<handle>.js, so a value only (a) knows gets a
  // select here instead. (b) the image of the first variant carrying the value;
  // (c) the colour-word map, each word found an equal conic-gradient segment in
  // reading order. SW_WORDS and SW_HEXES must equal the snippet's lists:
  // scripts/cart-c4-harness/liquid-check.js fails the moment they differ.
  var SW_WORDS = 'white,black,pink,blue,navy,red,green,yellow,orange,purple,grey,gray,teal,brown,silver,gold,clear,ocean,blossom,smokey,amethyst,citrine,prasiolite'.split(',');
  var SW_HEXES = '#FFFFFF,#0A0A0A,#F472B6,#2563EB,#1E3A8A,#DC2626,#16A34A,#FACC15,#F97316,#7C3AED,#9CA3AF,#9CA3AF,#0D9488,#92400E,#D1D5DB,#D4A017,#F4F4F0,#7986A8,#B88AA7,#443D39,#8F5BAB,#FDBF43,#09CFC2'.split(',');

  function isColourOption(name) {
    var n = String(name || '').toLowerCase();
    return n.indexOf('colour') > -1 || n.indexOf('color') > -1;
  }

  function swatchOf(p, i, value) {
    var text = String(value);
    for (var k = 0; k < p.variants.length; k++) {
      var v = p.variants[k], im = v.featured_image && (v.featured_image.src || v.featured_image);
      if ((v.options || [])[i] === text && im) return { img: im };
    }
    var probe = text.toLowerCase() + ' ', hits = [];
    SW_WORDS.forEach(function (w, n) {
      var at = probe.indexOf(w);
      if (at > -1) hits.push({ key: ('000000' + at).slice(-6) + ':' + SW_HEXES[n], hex: SW_HEXES[n] });
    });
    if (!hits.length) return null;
    hits.sort(function (a, b) { return a.key < b.key ? -1 : a.key > b.key ? 1 : 0; });
    if (hits.length === 1) return { bg: hits[0].hex, light: probe.indexOf('white') > -1 || probe.indexOf('clear') > -1 };
    var deg = Math.floor(360 / hits.length);
    return { bg: 'conic-gradient(' + hits.map(function (h, n) {
      return h.hex + ' ' + (deg * n) + 'deg ' + (n === hits.length - 1 ? 360 : deg * (n + 1)) + 'deg';
    }).join(', ') + ')' };
  }

  // Swatches only when the option is a colour AND every value resolves; one
  // unknown value and the whole option is a select (no half-swatch groups).
  function swatchesFor(p, op, i) {
    if (!isColourOption(op.name)) return null;
    var faces = op.values.map(function (val) { return swatchOf(p, i, val); });
    return faces.every(Boolean) ? faces : null;
  }

  function shutWord(s) { return s === 'pre' ? 'pre-order' : 'sold out'; }

  function chooserHtml(o, title) {
    var ch = state.chooser;
    var id = chooserId(o);
    var head = '<div class="c4__ch" id="' + id + '" role="group" aria-label="' + esc('Options for ' + title) + '"';
    if (ch.status === 'loading') return head + ' tabindex="-1" aria-busy="true"><p class="c4__chm">Loading the options.</p></div>';
    var p = productData[handleOf(o)];
    if (ch.status !== 'ready' || !p) {
      return head + ' tabindex="-1"><p class="c4__chm">The options did not load. <a href="' + esc(o.url) + '">See them on the product page</a></p></div>';
    }
    var sel = selOf(o, p);
    // selects side by side in one group (three packs read across), swatches full width
    var out = head + '>', group = '';
    var flush = function () { if (group) out += '<div class="c4__chg">' + group + '</div>'; group = ''; };
    optionsOf(p).forEach(function (op, i) {
      var states = op.values.map(function (val) { return valueState(o, p, sel, i, val); });
      var faces = swatchesFor(p, op, i);
      if (faces) {
        flush();
        out += '<fieldset class="c4__chf"><legend class="c4__chn">' + esc(op.name) + ': <b>' + esc(sel[i]) + '</b></legend><div class="c4__sws">' +
          op.values.map(function (val, k) {
            var off = states[k] !== 'ok', name = val + (off ? ', ' + shutWord(states[k]) : '');
            var face = faces[k].img
              ? '<img class="c4__swc" src="' + esc(img(faces[k].img, 96)) + '" alt="" aria-hidden="true">'
              : '<span class="c4__swc' + (faces[k].light ? ' c4__swc--light' : '') + '" style="background: ' + esc(faces[k].bg) + ';" aria-hidden="true"></span>';
            return '<label class="c4__sw' + (off ? ' is-off' : '') + '" title="' + esc(name) + '">' +
              '<input type="radio" class="c4__swi" name="' + id + '-' + i + '" value="' + esc(val) + '" data-c4-opt="' + i + '"' +
              (val === sel[i] ? ' checked' : '') + (off ? ' disabled' : '') + '>' + face + '<span class="c4__sr">' + esc(name) + '</span></label>';
          }).join('') + '</div></fieldset>';
      } else {
        group += '<div class="c4__chs"><label class="c4__chn" for="' + id + '-' + i + '">' + esc(op.name) + '</label>' +
          '<select class="c4__sel" id="' + id + '-' + i + '" data-c4-opt="' + i + '">' +
          op.values.map(function (val, k) {
            var off = states[k] !== 'ok';
            return '<option value="' + esc(val) + '"' + (val === sel[i] ? ' selected' : '') + (off ? ' disabled' : '') + '>' + esc(val + (off ? ', ' + shutWord(states[k]) : '')) + '</option>';
          }).join('') + '</select></div>';
      }
    });
    flush();
    return out + '</div>';
  }

  function offerRowEl(tag) {
    return root.querySelector('.c4__offer[data-offer-row="' + (window.CSS && CSS.escape ? CSS.escape(tag) : tag) + '"]');
  }

  // A chooser change re-draws its own row only, then puts focus back.
  function rerenderRow(tag, fk) {
    var row = offerRowEl(tag);
    var o = offerByTag(tag);
    if (!row || !o || !state.cart) { render(); return; }
    var tmp = document.createElement('div');
    tmp.innerHTML = offerCard(o, row.classList.contains('c4__offer--feat'), figures(state.cart));
    row.parentNode.replaceChild(tmp.firstChild, row);
    if (fk) restoreFocus(fk);
  }

  function rowHasFocus(tag) {
    var row = offerRowEl(tag);
    return !!row && row.contains(document.activeElement);
  }

  function openChooser(tag) {
    var o = offerByTag(tag);
    if (!canChoose(o)) return;
    if (state.chooser && state.chooser.tag !== tag) closeChooser(false);
    var h = handleOf(o);
    state.chooser = { tag: tag, pid: o.product_id, status: productData[h] ? 'ready' : 'loading' };
    rerenderRow(tag, { k: 'chooser', tag: tag });
    if (productData[h]) return;
    var done = function (status) {
      var ch = state.chooser;
      if (!ch || ch.tag !== tag || ch.status !== 'loading') return;
      ch.status = status;
      rerenderRow(tag, rowHasFocus(tag) ? { k: 'chooser', tag: tag } : null);
    };
    loadProduct(h).then(function (p) { done(Number(p.id) === Number(o.product_id) ? 'ready' : 'error'); }, function () { done('error'); });
  }

  function closeChooser(focusToggle) {
    var ch = state.chooser;
    if (!ch) return;
    state.chooser = null;
    rerenderRow(ch.tag, focusToggle ? { k: 'choose', tag: ch.tag } : null);
  }

  function chooseValue(control) {
    var row = control.closest('[data-offer-row]');
    var tag = row && row.dataset.offerRow;
    if (!tag) return;
    var o = offerByTag(tag);
    var p = o && productData[handleOf(o)];
    var i = Number(control.dataset.c4Opt);
    var fk = { k: 'opt', tag: tag, i: i };
    if (!o || !p || state.busy || !state.cart) { rerenderRow(tag, fk); return; }
    var sel = selOf(o, p);
    sel[i] = control.value;
    sel = settleSel(o, p, sel, i);
    var v = variantOf(p, sel);
    if (!v) { rerenderRow(tag, fk); return; }
    state.choices[tag] = { pid: o.product_id, vid: v.id };
    rerenderRow(tag, fk);
    announce(nice(o.title) + ', ' + v.title + ', ' + money(v.price) + (choosable(o, v) ? '.' : ', sold out.'));
  }

  function renderCode(cart, f) {
    var c = codes(cart);
    var limited = limitedLines(cart).length > 0;
    var limitedSaid = false;
    var html = '';
    if (c.applied.length) {
      html += c.applied.map(function (k) {
        var up = k.code.toUpperCase();
        return '<div class="c4__chip" data-tone="ok"><span class="c4__ic c4__ic--g" aria-hidden="true">' + tick() + '</span>' +
          '<span class="c4__chip-t" title="' + esc(up) + '"><b class="c4__chip-code">' + esc(up) + '</b>' + (up.length > 12 ? '' : '<span class="c4__chip-ap">applied</span>') + '</span>' +
          '<span class="c4__chip-v" aria-label="' + esc('saves ' + money(f.saved)) + '">' + money(-f.saved) + '</span>' +
          (state.codeOpen ? '' : '<button type="button" class="c4__chip-c" data-c4-code-toggle aria-expanded="false" aria-label="Change code ' + esc(up) + '">Change</button>') +
          '<button type="button" class="c4__chip-x" data-c4-code-remove aria-label="Remove code ' + esc(up) + '">' + cross() + '</button></div>';
      }).join('');
    }
    // Not beside a refused code's note: "applied" over "hasn't been used" reads as a glitch, and that
    // note already names the code still on the cart. It comes back with the next action.
    if (codeSkipsLimited(cart) && !(state.note && state.note.limited)) html += '<div class="c4__note" data-tone="ok"><p>' + esc(LIMITED.applied) + '</p></div>';
    if (state.note) {
      html += '<div class="c4__note" data-tone="' + state.note.tone + '"><p>' + esc(state.note.text) + '</p>' +
        (state.note.keep ? '<button type="button" class="c4__keep" data-c4-keep="' + esc(state.note.keep) + '">Keep ' + esc(state.note.keep) + '</button>' : '') + '</div>';
      limitedSaid = !!state.note.limited;
    } else if (c.refused.length && !c.applied.length) {
      html += '<div class="c4__note" data-tone="warn"><p>' + esc(refusal(c.refused[0].code)) + '</p></div>';
      limitedSaid = limited;
    }
    if (!c.applied.length || state.codeOpen) {
      if (state.codeOpen) {
        html += '<form class="c4__codeform" data-c4-code-form novalidate>' +
          '<label class="c4__sr" for="c4-code-input">Discount code</label>' +
          '<input id="c4-code-input" name="code" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="Discount code" required>' +
          '<button type="submit" class="c4__apply">Apply</button></form>' +
          (c.applied.length && !state.note ? '<p class="c4__hint">Codes do not combine. A new code replaces ' + esc(c.applied[0].code.toUpperCase()) + ' only if it applies.</p>' : '');
      } else {
        html += '<button type="button" class="c4__codelink" data-c4-code-toggle aria-expanded="false">Have a code?</button>';
      }
      // Said before a code is tried; once a code is on the cart or refused, its own note has said it.
      if (limited && !c.applied.length && !limitedSaid) html += '<p class="c4__hint">' + esc(LIMITED.hint) + '</p>';
    }
    return html;
  }

  // /cart.js says only applicable: false, whatever the reason, so a cart holding a limited-release
  // line gets the limited-release sentence for every refused code, a misspelt one included.
  function refusal(code) {
    if (limitedLines(state.cart).length) return LIMITED.refused;
    return code.toUpperCase() + ' can’t be used on this cart. Check the spelling, or it may not cover these items.';
  }

  function renderTotals(cart, f) {
    var rows = '';
    // The code's saving is on the chip above, so a Subtotal row is only worth
    // its height when something else (an automatic order discount) also moved the
    // total. The gift discounts are line level, so they never add this row.
    var autos = (cart.cart_level_discount_applications || []).map(app).filter(function (a) { return a.type !== 'discount_code'; });
    if (autos.length) {
      rows += '<div class="c4__tr"><span>Subtotal</span><span>' + money(f.subtotal) + '</span></div>';
      autos.forEach(function (a) {
        rows += '<div class="c4__tr c4__tr--save"><span>' + esc(a.title) + '</span><span>' + money(-a.amount) + '</span></div>';
      });
    }
    var free = f.promise && !f.hasBulky && f.eligible >= (state.meta.threshold_cents || Infinity);
    rows += '<div class="c4__tr c4__tr--total"><span>Total<small>GST included. ' + (free ? 'Free AU shipping.' : 'Shipping at checkout.') + '</small></span><strong data-c4-total>' + money(f.total) + '</strong></div>';
    if (f.splitShown) {
      rows += '<div class="c4__due"><div><span>Due today</span><b>' + money(f.dueToday) + '</b></div><div><span>Balance later</span><b>' + money(f.later) + '</b></div></div>';
    } else if (f.hasDeposit) {
      rows += '<p class="c4__depnote">' + (f.depositsKnown ? 'Pre-order deposit <b>' + money(f.deposits) + '</b> is' : 'A pre-order deposit is') +
        ' charged today' + (cart.items.length > 1 ? ' with your other items' : '') + '. Checkout shows the exact amounts.</p>';
    }
    return rows;
  }

  function renderCheckout(f) {
    if (!el.checkout) return;
    // A deposit cart whose split is not proven gets a bare "Checkout": the button
    // must never promise a figure the payment page might not show.
    var amount = f.splitShown ? money(f.dueToday) + ' today' : (f.hasDeposit ? '' : money(f.total));
    el.checkout.innerHTML = '<span>' + lock() + ' Checkout</span>' + (amount ? '<span class="c4__co-amt">' + amount + '</span>' : '');
    el.checkout.classList.toggle('c4__checkout--bare', !amount);
    el.checkout.disabled = state.busy;
  }

  /* ---------- icons ---------- */
  function tick() { return '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'; }
  function cross() { return '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M3 3l6 6M9 3L3 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>'; }
  function minus() { return '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>'; }
  function plus() { return '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 6h8M6 2v8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>'; }
  function lock() { return '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><rect x="2.5" y="6" width="9" height="6.5" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M4.5 6V4.5a2.5 2.5 0 015 0V6" stroke="currentColor" stroke-width="1.4"/></svg>'; }
  function van() { return '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M1.5 4h8v7h-8zM9.5 7h3l2 2.5V11h-5" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><circle cx="4.5" cy="12" r="1.3" fill="currentColor"/><circle cx="12" cy="12" r="1.3" fill="currentColor"/></svg>'; }
  function giftIcon() { return '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 7.5h11v6h-11zM1.5 5h13v2.5h-13zM8 5v8.5M8 5C6.8 2.6 4.4 2.6 4.6 4.1 4.8 5 8 5 8 5zm0 0c1.2-2.4 3.6-2.4 3.4-.9C11.2 5 8 5 8 5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>'; }

  /* ---------- state changes ---------- */

  // Wallet buttons are rendered by Liquid at page load, and only when the cart
  // has items (an empty cart prints an empty container, so landing pages do not
  // load Shopify's wallet script). After every change the bundled section HTML
  // carries a fresh copy; swap it in when it differs so the accelerated-checkout
  // elements appear or re-initialise against the current cart.
  function swapExpress(node) {
    if (!node || !el.express) return;
    var fresh = node.querySelector('[data-c4-express]');
    if (!fresh) return;
    var html = fresh.innerHTML;
    if (el.express._c4src === undefined) el.express._c4src = el.express.innerHTML;
    if (html === el.express._c4src) return;
    var wasHidden = el.express.hidden;
    el.express.replaceWith(fresh);
    el.express = fresh;
    el.express._c4src = html;
    el.express.hidden = wasHidden;
    // The page prints no wallets while the cart is empty, so Shopify's module may not be loaded yet.
    // Shopify's own bootstrap watches the DOM and calls this when the first wallet element appears;
    // calling it here as well is harmless (init() replaces itself with a no-op after the first call).
    if (fresh.querySelector('shopify-accelerated-checkout-cart, shopify-accelerated-checkout') &&
        window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
      try { window.Shopify.PaymentButton.init(); } catch (e) { /* the MutationObserver route still applies */ }
    }
  }

  function settle(cart, sections) {
    var node = sectionNode(sections);
    if (node) {
      var meta = readMeta(node);
      if (meta) state.meta = meta;
      swapExpress(node);
    }
    if (cart) { state.cart = cart; recordCharges(cart); }
    state.busy = false;
    root.classList.remove('is-busy');
    render();
    return state.cart;
  }

  function begin() {
    state.busy = true;
    root.classList.add('is-busy');
    if (el.checkout) el.checkout.disabled = true;
  }

  // Honest errors: a refusal from Shopify (4xx) left the cart as it was; a
  // dropped connection may or may not have landed, so the cart is re-read and
  // the drawer says it is showing what is saved. opts.alone: Shopify's sentence
  // already says what happened to the cart (a refused quantity), so nothing is
  // added to it; opts.after(cart) runs on the cart as re-read, before the render.
  function fail(err, opts) {
    opts = opts || {};
    state.busy = false;
    root.classList.remove('is-busy');
    var refused = err && err.status && err.status >= 400 && err.status < 500;
    var said = err && (err.description || err.message);
    var text = refused
      ? (opts.alone ? (said || 'That quantity is not available.') : ((said || 'That could not be done.') + ' Your cart was not changed.'))
      : 'We could not reach the store. The cart below is what is saved.';
    state.note = { tone: 'err', text: text };
    render();
    announce(text);
    return getCart().then(function (c) {
      state.cart = c;
      if (opts.after) opts.after(c);
      render();
      return reconcile({ allowAdd: false });
    }).catch(function () {});
  }

  function announce(text) {
    if (!el.live || !text) return;
    el.live.textContent = '';
    var set = function () { el.live.textContent = text; };
    if (window.requestAnimationFrame) requestAnimationFrame(function () { setTimeout(set, 30); }); else setTimeout(set, 50);
  }

  // Every user action ends here: the gift ladder runs on the settled cart, then
  // one sentence goes to the live region (the action's own, then the gift's).
  function finish(base) {
    return reconcile().then(function (msg) {
      var t = typeof base === 'function' ? base() : base;
      announce([t, msg].filter(Boolean).join(' '));
      return state.cart;
    });
  }

  function startAction() {
    state.note = null;
    state.giftNote = null;
  }

  // Open (and the autoload): read the cart and the section afresh. Never rejects: a failed
  // /cart.js read says so in the drawer (loadFailed) instead of leaving the skeleton up.
  function refresh() {
    state.limits = {};
    return Promise.all([getCart(), getSection().catch(function () { return null; })]).then(function (r) {
      state.cart = r[0];
      var node = r[1];
      if (node) {
        var meta = readMeta(node);
        if (meta) state.meta = meta;
        // The cart may have been filled outside this page (another tab, Back), and this page
        // printed no wallets for an empty cart: the fresh render's wallets go in now.
        swapExpress(node);
      }
      render();
      return reconcile().then(function (msg) { if (msg) announce(msg); return state.cart; });
    }, loadFailed);
  }

  // /cart.js could not be read. With no cart shown yet, or an empty one (it cannot be trusted
  // after an add): a short message, Try again, and the cart page (Checkout in the footer still
  // works, see the click handler). With lines already shown: they stay, under a note that they
  // may be out of date.
  function loadFailed(err) {
    warn('could not read the cart.', err && (err.description || err.message));
    var text;
    if (!state.cart || !state.cart.item_count) {
      text = 'We could not load your cart.';
      el.body.innerHTML = '<div class="c4__empty"><p class="c4__empty-p">' + text + '</p><ul class="c4__chips" role="list">' +
        '<li><button type="button" class="c4__chiplink" data-c4-retry>Try again</button></li>' +
        '<li><a class="c4__chiplink" href="' + esc(routes().cart) + '">Go to the cart page</a></li></ul></div>';
    } else {
      text = 'We could not reach the store. The cart below may be out of date.';
      state.note = { tone: 'err', text: text };
      render();
    }
    announce(text);
    return state.cart;
  }

  function itemByKey(cart, key) { return (cart && cart.items || []).filter(function (i) { return i.key === key; })[0]; }

  // A line's identity across a change. Shopify gives a line a NEW key when a discount starts or
  // stops applying to it (a quantity crossing a gift tier), and splits a line whose units are
  // only partly discounted into two, so a line is found again by variant, selling plan and
  // properties, never by its old key.
  function planOf(it) { var a = it && it.selling_plan_allocation; return (a && a.selling_plan && a.selling_plan.id) || null; }
  function lineId(it) { return String(it.variant_id) + '|' + planOf(it) + '|' + JSON.stringify(it.properties || {}); }
  function sameLine(a, b) { return lineId(a) === lineId(b); }

  function changeLine(key, qty, extra) {
    var before = itemByKey(state.cart, key);
    var title = before ? nice(before.product_title) : 'Item';
    begin();
    startAction();
    return api(routes().change + '.js', { id: key, quantity: qty })
      .then(function (j) {
        settle(j, j.sections);
        return finish(function () {
          // summed across a split; a Remove (quantity 0) is always "removed"
          var now = qty > 0 && before ? state.cart.items.filter(function (i) { return sameLine(i, before); }) : [];
          var n = sum(now, function (i) { return i.quantity; });
          var t = (now.length ? title + ', quantity ' + n + '.' : title + ' removed.') + (extra ? ' ' + extra : '');
          return t + ' Total ' + money(state.cart.total_price) + '.';
        });
      }).catch(function (err) {
        var refused = err && err.status >= 400 && err.status < 500;
        if (!(refused && before && qty > before.quantity)) return fail(err);
        // Shopify refused more of this line (stock, or a quantity rule). Its own sentence says
        // what happened ("Only 1 item was added to your cart due to availability."), so it is
        // shown alone, and + is shut at whatever the cart holds once it is read again.
        return fail(err, { alone: true, after: function (c) {
          var held = sum(c.items.filter(function (i) { return sameLine(i, before); }), function (i) { return i.quantity; });
          if (held > 0) state.limits[lineId(before)] = held;
        } });
      });
  }

  function setCode(code) {
    return api(routes().update + '.js', { discount: code });
  }

  function applyCode(raw, opts) {
    opts = opts || {};
    var code = String(raw || '').trim();
    if (!code) return Promise.resolve();
    var up = code.toUpperCase();
    var before = codes(state.cart).applied.map(function (c) { return c.code; });
    var savedBefore = codeSavings(state.cart);
    if (before.some(function (c) { return c.toUpperCase() === up; })) {
      state.note = { tone: 'ok', text: up + ' is already applied.' };
      state.codeOpen = false;
      state.focusKey = { k: 'code' };
      render();
      return Promise.resolve();
    }
    begin();
    state.giftNote = null;
    state.focusKey = { k: 'code' };
    return setCode(code).then(function (j) {
      var hit = (j.discount_codes || []).filter(function (c) { return c.code.toUpperCase() === up; })[0];
      if (hit && hit.applicable) {
        state.codeOpen = false;
        var oldUp = before.length ? before[0].toUpperCase() : null;
        var savedAfter = codeSavings(j);
        if (oldUp && !opts.noCompare && savedAfter < savedBefore) {
          // Codes do not combine: say plainly when the new one is worth less.
          state.note = { tone: 'warn', text: up + ' saves ' + money(savedAfter) + ', ' + oldUp + ' saved ' + money(savedBefore) + '. Keep ' + oldUp + '?', keep: oldUp };
        } else if (oldUp) {
          state.note = { tone: 'ok', text: up + ' replaced ' + oldUp + '. Codes do not combine.' };
        } else {
          state.note = null;
        }
        settle(j, j.sections);
        return finish(function () { return (state.note ? state.note.text + ' ' : up + ' applied. ') + (codeSkipsLimited(state.cart) ? LIMITED.applied + ' ' : '') + 'Total ' + money(state.cart.total_price) + '.'; });
      }
      // Not applicable (unknown, expired, or not for these items). update.js
      // REPLACED the old code, so put it back, or clear the dead one, and only
      // say the old code is back once the response shows it is.
      var msg = refusal(code);
      var saysLimited = limitedLines(state.cart).length > 0;
      return setCode(before.join(',')).then(function (j2) {
        var restored = before.length && (j2.discount_codes || []).some(function (c) { return c.applicable && c.code.toUpperCase() === before[0].toUpperCase(); });
        state.codeOpen = true;
        state.note = { tone: 'warn', limited: saysLimited, text: !before.length ? msg : restored ? msg + ' ' + before[0].toUpperCase() + ' is still applied.' : msg + ' ' + before[0].toUpperCase() + ' could not be put back. Enter it again to reapply it.' };
        settle(j2, j2.sections);
        return finish(state.note.text);
      });
    }).catch(fail);
  }

  function removeCode() {
    begin();
    startAction();
    state.focusKey = { k: 'code' };
    return setCode('').then(function (j) {
      settle(j, j.sections);
      return finish(function () { return 'Code removed. Total ' + money(state.cart.total_price) + '.'; });
    }).catch(fail);
  }

  function addOffer(variantId, offerTag, explore, pinned) {
    begin();
    startAction();
    var props = { _pk_offer: offerTag || '' };
    if (state.meta && state.meta.generation) props._pk_gen = state.meta.generation;
    if (explore) props._pk_explore = '1';
    if (pinned) props._pk_pin = '1';
    // a variant picked in the row's chooser, not the one offered: read apart
    var offered = offerByTag(offerTag);
    if (offered && Number(variantId) !== Number(offered.variant_id)) props._pk_chose = '1';
    return api(routes().add + '.js', { items: [{ id: Number(variantId), quantity: 1, properties: props }] })
      .then(function (j) {
        var sections = j.sections;
        delete state.choices[offerTag];
        if (state.chooser && state.chooser.tag === offerTag) state.chooser = null;
        return getCart().then(function (c) {
          settle(c, sections);
          var added = (j.items || [])[0];
          return finish(function () { return (added ? nice(added.product_title) + ' added. ' : 'Added. ') + 'Total ' + money(state.cart.total_price) + '.'; });
        });
      }).catch(fail);
  }

  // Keyed by the FROM line's key, never its variant id, so a variant that sits
  // on two lines (a plain one and one carrying _pk_offer) is never ambiguous.
  function swap(fromVariant, toVariant) {
    var lines = state.cart.items.filter(function (i) { return String(i.variant_id) === String(fromVariant) && !giftOf(i); });
    var already = state.cart.items.some(function (i) { return String(i.variant_id) === String(toVariant); });
    if (lines.length !== 1 || already) {
      state.note = { tone: 'warn', text: 'That swap no longer fits this cart.' };
      return refresh();
    }
    begin();
    startAction();
    var updates = {};
    updates[lines[0].key] = 0;
    updates[toVariant] = 1;
    return api(routes().update + '.js', { updates: updates })
      .then(function (j) {
        settle(j, j.sections);
        return finish(function () { return 'Swapped. Total ' + money(state.cart.total_price) + '.'; });
      }).catch(fail);
  }

  /* ---------- open, close, focus ---------- */

  // Everything outside the drawer is inert while it is open, so a screen
  // reader and Tab cannot leave the dialog.
  function setInert(on) {
    if (!on) {
      state.inerted.forEach(function (n) { n.inert = false; n.removeAttribute('inert'); });
      state.inerted = [];
      return;
    }
    var n = root;
    while (n && n.parentElement && n !== document.body) {
      var parent = n.parentElement;
      Array.prototype.forEach.call(parent.children, function (sib) {
        if (sib === n || sib.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sib.tagName)) return;
        sib.inert = true;
        sib.setAttribute('inert', '');
        state.inerted.push(sib);
      });
      n = parent;
    }
  }

  function open() {
    if (root.classList.contains('is-open')) return;
    state.lastFocused = document.activeElement;
    if (!state.cart) renderSkeleton();
    root.setAttribute('aria-hidden', 'false');
    root.classList.add('is-open');
    document.body.classList.add('pickld-cdrawer-open');
    setInert(true);
    setTimeout(function () {
      if (root.classList.contains('is-open') && !root.contains(document.activeElement)) el.close.focus({ preventScroll: true });
    }, 320);
  }

  function close() {
    root.setAttribute('aria-hidden', 'true');
    root.classList.remove('is-open');
    document.body.classList.remove('pickld-cdrawer-open');
    setInert(false);
    if (state.lastFocused && state.lastFocused.focus) state.lastFocused.focus();
  }

  /* ---------- events ---------- */

  root.addEventListener('click', function (e) {
    var t = e.target;
    if (t.closest('[data-c4-close]')) { close(); return; }
    if (t.closest('[data-c4-retry]')) { renderSkeleton(); refresh(); return; }
    // No cart read yet (still loading, or /cart.js failed): the drawer's own buttons wait, but
    // Checkout goes through. Its form posts to the cart page, and checkout reads the cart itself.
    if (!state.cart) { if (t.closest('button') && !t.closest('[data-c4-checkout]')) e.preventDefault(); return; }
    if (state.busy) { if (t.closest('button')) e.preventDefault(); return; }
    var fk = focusKeyOf(t);
    var line = t.closest('[data-key]');
    var q = t.closest('[data-c4-qty]');
    if (q && line) {
      var item = itemByKey(state.cart, line.dataset.key);
      state.focusKey = fk;
      if (item) changeLine(item.key, Math.max(0, item.quantity + Number(q.dataset.c4Qty)));
      return;
    }
    if (t.closest('[data-c4-remove]') && line) {
      state.focusKey = fk;
      var gone = itemByKey(state.cart, line.dataset.key);
      var h = giftOf(gone);
      if (h) {
        // The shopper said no to this gift: never added again this session.
        var memo = memoFor(state.cart);
        memo.declined[h] = true;
        memo.added[h] = false;
        memoSave(memo);
        changeLine(line.dataset.key, 0, 'It will not be added again on this visit.');
      } else {
        changeLine(line.dataset.key, 0);
      }
      return;
    }
    if (t.closest('[data-c4-code-toggle]')) {
      state.codeOpen = true; state.note = null; render();
      var input = root.querySelector('#c4-code-input'); if (input) input.focus();
      return;
    }
    if (t.closest('[data-c4-code-remove]')) { removeCode(); return; }
    var keep = t.closest('[data-c4-keep]');
    if (keep) { state.note = null; applyCode(keep.dataset.c4Keep, { noCompare: true }); return; }
    var chooseBtn = t.closest('[data-c4-choose]');
    if (chooseBtn) {
      var crow = chooseBtn.closest('[data-offer-row]');
      var ctag = crow && crow.dataset.offerRow;
      if (state.chooser && state.chooser.tag === ctag) closeChooser(true); else openChooser(ctag);
      return;
    }
    var add = t.closest('[data-c4-add]');
    if (add) { state.focusKey = fk; add.textContent = 'Adding'; addOffer(add.dataset.variant, add.dataset.offer, add.dataset.explore === '1', add.dataset.pin === '1'); return; }
    var sw = t.closest('[data-c4-swap]');
    if (sw) { state.focusKey = fk; sw.textContent = 'Swapping'; swap(sw.dataset.from, sw.dataset.to); return; }
  });

  // A swatch or select in a row's chooser: the row follows the choice.
  root.addEventListener('change', function (e) {
    var c = e.target && e.target.closest ? e.target.closest('[data-c4-opt]') : null;
    if (c) chooseValue(c);
  });

  root.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-c4-code-form]');
    if (!form) return;
    e.preventDefault();
    if (state.busy || !state.cart) return;
    applyCode(form.elements.code.value);
  });

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-pickld-cart-toggle]');
    if (!toggle) return;
    e.preventDefault();
    open();
    refresh();
  });

  document.addEventListener('keydown', function (e) {
    if (!root.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      // Escape inside an open chooser closes the chooser, not the drawer.
      var inRow = state.chooser && document.activeElement && document.activeElement.closest ? document.activeElement.closest('[data-offer-row]') : null;
      if (inRow && inRow.dataset.offerRow === state.chooser.tag) { e.preventDefault(); closeChooser(true); return; }
      close(); return;
    }
    if (e.key !== 'Tab') return;
    var f = Array.prototype.filter.call(el.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'), function (n) { return n.offsetParent !== null; });
    if (!f.length) return;
    if (!el.panel.contains(document.activeElement)) { e.preventDefault(); f[0].focus(); return; }
    if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
    else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
  });

  // The pinned row depends on the viewport: re-render when a phone rotates.
  if (phoneQuery) {
    var onPhone = function () { if (state.cart) render(); };
    if (phoneQuery.addEventListener) phoneQuery.addEventListener('change', onPhone);
    else if (phoneQuery.addListener) phoneQuery.addListener(onPhone);
  }

  window.PickldCart = {
    open: function () { open(); return refresh(); },
    close: close,
    refresh: refresh,
    // Kept for assets/pickld-buy-row.js, the PDP and the paddle finder: a
    // FormData add that opens the drawer. It resolves with the add.js JSON and
    // rejects with an Error carrying Shopify's description, which every caller
    // already handles (the PDP prints err.description, the buy row and the
    // finder send the shopper to the PDP).
    add: function (formData) {
      formData.append('sections', sectionId);
      formData.append('sections_url', window.location.pathname);
      begin();
      startAction();
      return fetch(routes().add + '.js', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: formData })
        .then(parse)
        .then(function (j) {
          recordCharges(j);
          var sections = j.sections;
          // Open on the cart as added, then let the gift ladder run in front of the
          // shopper (the drawer is busy, Checkout disabled, until it settles). If the
          // cart cannot be read, the drawer says so (loadFailed), never a skeleton.
          var unread = null;
          return getCart().then(function (c) { settle(c, sections); }, function (e) { unread = e || new Error('cart read failed'); settle(null, sections); })
            .then(function () { open(); if (unread) { loadFailed(unread); return ''; } return reconcile(); })
            .then(function (msg) { if (msg) announce(msg); return j; });
        }, function (err) {
          state.busy = false;
          root.classList.remove('is-busy');
          getCart().then(function (c) { state.cart = c; render(); }).catch(function () {});
          throw err;
        });
    },
    _state: state,
    // exposed for the harness self-tests only (scripts/cart-c4-harness)
    _test: {
      codeSavings: codeSavings, app: app, figures: figures, planGifts: planGifts, giftTarget: giftTarget,
      giftFigures: giftFigures, giftFree: giftFree, dispatchText: dispatchText,
      memoFor: memoFor, codeKey: codeKey, variantLabel: variantLabel, sameLine: sameLine,
      reloadToMatch: reloadToMatch, reloadReset: reloadReset,
      setMeta: function (m) { state.meta = m; },
      chooser: { optionsOf: optionsOf, valueState: valueState, settleSel: settleSel, variantOf: variantOf, choosable: choosable,
        swatchOf: swatchOf, swatchesFor: swatchesFor, isColourOption: isColourOption, canChoose: canChoose, offerCard: offerCard,
        setProduct: function (h, p) { productData[h] = p; productCache[h] = Promise.resolve(p); } }
    }
  };

  if (root.hasAttribute('data-c4-autoload')) refresh();
  else loadCheck();
})();
